import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useLocation, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Star, Menu, X, Phone, Mail, Instagram, Facebook, Twitter, Calendar, MessageSquare, Loader2, ShoppingCart, Plus, Minus, Trash2, Check, BarChart3, Users, Package, DollarSign, Clock, CalendarCheck, PhoneCall, MessageCircle, Send, Sparkles, Grid, List, AlertCircle } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions for date/time formatting
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatDateWithDay = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const dateFormatted = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  return `${dayName}, ${dateFormatted}`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const date = new Date(dateTimeStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) + ' ' +
         date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Phone number formatting functions
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  // Format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    // Handle +1 prefix
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  // Return original if not a standard format
  return phone;
};

const formatPhoneInput = (value) => {
  // Remove all non-numeric characters
  const cleaned = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = cleaned.slice(0, 10);
  
  // Format as user types
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Image Upload Component with Drag & Drop
const ImageUpload = ({ value, onChange, label = "Image" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onChange(response.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="font-manrope text-neutral-400 text-sm">{label}</label>
      
      {value ? (
        <div className="relative group">
          <img 
            src={value} 
            alt="Preview" 
            className="w-full h-40 object-cover rounded-lg border border-white/10"
          />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30 transition-all"
            >
              Change
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="px-3 py-1.5 bg-red-500/50 text-white rounded-lg text-sm hover:bg-red-500/70 transition-all"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
            isDragging 
              ? 'border-gold bg-gold/10' 
              : 'border-white/20 hover:border-gold/50 hover:bg-white/5'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={32} className="text-gold animate-spin" />
              <p className="font-manrope text-neutral-400 text-sm">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Plus size={24} className="text-gold" />
              </div>
              <p className="font-manrope text-white text-sm">
                Drag & drop an image here
              </p>
              <p className="font-manrope text-neutral-500 text-xs">
                or click to browse
              </p>
              <p className="font-manrope text-neutral-600 text-xs">
                JPEG, PNG, GIF, WebP (max 5MB)
              </p>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <p className="text-red-400 text-xs font-manrope">{error}</p>
      )}
      
      {/* URL Input as fallback */}
      <div className="flex items-center gap-2">
        <span className="font-manrope text-neutral-500 text-xs">or paste URL:</span>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="flex-1 px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
        />
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

const BookingModal = ({ isOpen, onClose, onChatClick }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    preferred_date: null,
    preferred_time: "",
    service_type: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [services, setServices] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(60);

  // Fetch services when modal opens
  useEffect(() => {
    if (isOpen) {
      axios.get(`${API}/services`)
        .then(res => setServices(res.data))
        .catch(err => console.error('Failed to fetch services:', err));
    }
  }, [isOpen]);

  // Update duration when service changes
  useEffect(() => {
    if (formData.service_type && services.length > 0) {
      const service = services.find(s => s.name.toLowerCase() === formData.service_type.toLowerCase());
      if (service) {
        setSelectedDuration(service.duration_minutes || 60);
        // Reset time when service changes since duration affects availability
        if (formData.preferred_time) {
          setFormData(prev => ({ ...prev, preferred_time: '' }));
        }
      }
    }
  }, [formData.service_type, services]);

  // Fetch calendar availability when month changes
  useEffect(() => {
    if (isOpen && selectedOption === 'calendar') {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      axios.get(`${API}/availability/calendar/${year}/${month}`)
        .then(res => setCalendarData(res.data))
        .catch(err => console.error('Failed to fetch calendar:', err));
    }
  }, [isOpen, selectedOption, currentMonth]);

  // Fetch available slots when date is selected - use selected duration
  useEffect(() => {
    if (formData.preferred_date) {
      setLoadingSlots(true);
      const dateStr = format(formData.preferred_date, 'yyyy-MM-dd');
      axios.get(`${API}/availability/slots/${dateStr}?duration=${selectedDuration}`)
        .then(res => {
          setAvailableSlots(res.data.available_slots || []);
          setLoadingSlots(false);
        })
        .catch(err => {
          console.error('Failed to fetch slots:', err);
          setLoadingSlots(false);
        });
    } else {
      setAvailableSlots([]);
    }
  }, [formData.preferred_date, selectedDuration]);

  // Get day status from calendar data
  const getDayStatus = (date) => {
    if (!calendarData) return 'loading';
    const day = calendarData.days.find(d => d.date === format(date, 'yyyy-MM-dd'));
    return day?.status || 'available';
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", message: "", preferred_date: null, preferred_time: "", service_type: "" });
    setAvailableSlots([]);
    setCalendarData(null);
    setSelectedDuration(60);
  };

  if (!isOpen) return null;

  const bookingOptions = [
    {
      id: "calendar",
      icon: Calendar,
      title: "Book by Calendar",
      description: "Schedule your appointment online",
      requiresEmail: true
    },
    {
      id: "chat",
      icon: MessageCircle,
      title: "Book by Chat",
      description: "Chat with our Hair Consultant",
      opensChat: true
    },
    {
      id: "text",
      icon: MessageSquare,
      title: "Book by Text",
      description: "(619) 759-7487",
      requiresPhone: true,
      noAction: true
    },
    {
      id: "call",
      icon: Phone,
      title: "Book by Call",
      description: "(619) 759-7487",
      requiresPhone: true
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Format date for API submission
      const submitData = {
        ...formData,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, 'yyyy-MM-dd') : null,
        duration_minutes: selectedDuration
      };
      const response = await axios.post(`${API}/booking/${selectedOption}`, submitData);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSelectedOption(null);
        resetForm();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (formData.preferred_date && !formData.preferred_time) {
      // If date selected but no time, go back to date selection
      setFormData({ ...formData, preferred_date: null, preferred_time: "" });
      setAvailableSlots([]);
    } else if (formData.preferred_time) {
      // If time selected, go back to time selection
      setFormData({ ...formData, preferred_time: "" });
    } else {
      // Otherwise, go back to booking options
      setSelectedOption(null);
      resetForm();
    }
    setError(null);
  };

  // Navigate calendar months
  const goToPrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Check if prev month button should be disabled
  const isPrevMonthDisabled = () => {
    const today = new Date();
    return currentMonth.getFullYear() === today.getFullYear() && 
           currentMonth.getMonth() <= today.getMonth();
  };

  return (
    <div 
      data-testid="booking-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        data-testid="booking-modal"
        className={`bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto ${
          selectedOption === 'calendar' ? 'max-w-2xl' : 'max-w-md'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {selectedOption ? (
            <button onClick={handleBack} className="text-neutral-400 hover:text-white transition-colors">
              <ChevronLeft size={24} />
            </button>
          ) : (
            <h2 className="font-playfair text-2xl text-white">Book a <span className="text-gold">Seat</span></h2>
          )}
          <button 
            data-testid="booking-modal-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-playfair text-xl text-white mb-2">Booking Confirmed!</h3>
            <p className="font-manrope text-neutral-400 text-sm">We'll see you soon!</p>
          </div>
        ) : !selectedOption ? (
          /* Booking Options */
          <div className="flex flex-col gap-4">
            {bookingOptions.map((option) => (
              option.opensChat ? (
                /* Book by Chat - Opens the chat interface */
                <button
                  key={option.id}
                  data-testid={`booking-option-${option.id}`}
                  onClick={() => {
                    onClose();
                    onChatClick && onChatClick();
                  }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-gold/50 hover:bg-gold/5 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <option.icon size={22} className="text-gold" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-manrope text-white font-medium">{option.title}</h3>
                    <p className="font-manrope text-gold text-sm">{option.description}</p>
                  </div>
                </button>
              ) : (option.id === "call" || option.id === "text") ? (
                /* Book by Call/Text - Just display phone number, no action */
                <div
                  key={option.id}
                  data-testid={`booking-option-${option.id}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-gold/50 hover:bg-gold/5 transition-all duration-300 group cursor-default"
                >
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <option.icon size={22} className="text-gold" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-manrope text-white font-medium">{option.title}</h3>
                    <p className="font-manrope text-gold text-sm font-medium">{option.description}</p>
                  </div>
                </div>
              ) : (
                <button
                  key={option.id}
                  data-testid={`booking-option-${option.id}`}
                  onClick={() => setSelectedOption(option.id)}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-gold/50 hover:bg-gold/5 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <option.icon size={22} className="text-gold" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-manrope text-white font-medium">{option.title}</h3>
                    <p className="font-manrope text-neutral-400 text-sm">{option.description}</p>
                  </div>
                </button>
              )
            ))}
          </div>
        ) : (
          /* Booking Form */
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <h3 className="font-playfair text-xl text-white mb-2">
              {bookingOptions.find(o => o.id === selectedOption)?.title}
            </h3>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <input
              type="text"
              placeholder="Your Name *"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
            />

            <input
              type="tel"
              placeholder="Mobile Number *"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: formatPhoneInput(e.target.value) })}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
            />

            {(selectedOption === "calendar") && (
              <input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
              />
            )}

            {/* Service Selection with Duration Info */}
            <div>
              <select
                value={formData.service_type}
                onChange={(e) => {
                  setFormData({ ...formData, service_type: e.target.value, preferred_date: null, preferred_time: '' });
                }}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-gold/50 focus:outline-none transition-colors"
              >
                <option value="" className="bg-[#1a1a1a]">Select Service *</option>
                {services.filter(s => s.is_active).map(service => {
                  const mins = service.duration_minutes || 60;
                  const durationText = mins >= 60 
                    ? (mins % 60 > 0 ? `${Math.floor(mins / 60)}hr ${mins % 60}min` : `${Math.floor(mins / 60)}hr${Math.floor(mins / 60) > 1 ? 's' : ''}`)
                    : `${mins}min`;
                  return (
                    <option key={service.id} value={service.name.toLowerCase()} className="bg-[#1a1a1a]">
                      {service.name} ({durationText})
                    </option>
                  );
                })}
              </select>
              {formData.service_type && selectedDuration > 0 && (
                <p className="font-manrope text-gold text-xs mt-1 flex items-center gap-1">
                  <Clock size={12} />
                  This service takes approximately {selectedDuration >= 60 ? `${Math.floor(selectedDuration / 60)}hr${Math.floor(selectedDuration / 60) > 1 ? 's' : ''}${selectedDuration % 60 > 0 ? ` ${selectedDuration % 60}min` : ''}` : `${selectedDuration}min`}
                </p>
              )}
            </div>

            {selectedOption === "calendar" && formData.service_type && (
              <div className="space-y-4">
                {/* Step 1: Date Selection - Calendly Style */}
                {!formData.preferred_date && (
                  <div>
                    <p className="font-manrope text-neutral-400 text-sm mb-3">Select a Date</p>
                    
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        type="button"
                        onClick={goToPrevMonth}
                        disabled={isPrevMonthDisabled()}
                        className="p-2 rounded-lg bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <h3 className="font-playfair text-lg text-white">
                        {format(currentMonth, 'MMMM yyyy')}
                      </h3>
                      <button
                        type="button"
                        onClick={goToNextMonth}
                        className="p-2 rounded-lg bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 transition-all"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="text-center text-xs text-neutral-500 font-manrope py-2">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1">
                      {calendarData?.days ? (
                        <>
                          {/* Empty cells for first week alignment */}
                          {Array.from({ length: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() }).map((_, i) => (
                            <div key={`empty-${i}`} className="h-10" />
                          ))}
                          
                          {calendarData.days.map((day) => {
                            const isDisabled = day.status === 'past' || day.status === 'blocked' || day.status === 'full';
                            const isLimited = day.status === 'limited';
                            
                            return (
                              <button
                                key={day.date}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => {
                                  const dateObj = new Date(day.date + 'T00:00:00');
                                  setFormData({ ...formData, preferred_date: dateObj, preferred_time: '' });
                                }}
                                className={`h-10 rounded-lg font-manrope text-sm transition-all relative ${
                                  isDisabled
                                    ? 'text-neutral-600 cursor-not-allowed'
                                    : isLimited
                                    ? 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/30'
                                    : 'bg-white/5 text-white hover:bg-gold/20 hover:text-gold border border-white/10 hover:border-gold/50'
                                }`}
                              >
                                {day.day}
                                {day.status === 'available' && day.available_slots > 0 && (
                                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" />
                                )}
                              </button>
                            );
                          })}
                        </>
                      ) : (
                        <div className="col-span-7 py-8 text-center">
                          <Loader2 className="animate-spin mx-auto text-gold" size={24} />
                        </div>
                      )}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-4 text-xs font-manrope">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-neutral-400">Available</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span className="text-neutral-400">Limited</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-neutral-600" />
                        <span className="text-neutral-400">Unavailable</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Time Selection */}
                {formData.preferred_date && !formData.preferred_time && (
                  <div>
                    <p className="font-manrope text-neutral-400 text-sm mb-1">
                      Select a Time for {format(formData.preferred_date, 'EEEE, MMMM d, yyyy')}
                    </p>
                    
                    {loadingSlots ? (
                      <div className="py-8 text-center">
                        <Loader2 className="animate-spin mx-auto text-gold" size={24} />
                        <p className="text-neutral-400 text-sm mt-2">Loading available times...</p>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 mt-3 max-h-64 overflow-y-auto pr-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            onClick={() => setFormData({ ...formData, preferred_time: slot.time })}
                            className="px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-manrope text-sm hover:bg-gold/20 hover:border-gold/50 hover:text-gold transition-all"
                          >
                            {slot.display_time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center bg-red-500/10 rounded-lg border border-red-500/30">
                        <AlertCircle className="mx-auto text-red-400 mb-2" size={24} />
                        <p className="text-red-400 text-sm">No available times for this date.</p>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, preferred_date: null })}
                          className="text-gold text-sm mt-2 hover:underline"
                        >
                          Select a different date
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Step 3: Confirmation Summary */}
                {formData.preferred_date && formData.preferred_time && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={18} className="text-green-400" />
                      <p className="font-manrope text-green-400 font-medium">Appointment Selected</p>
                    </div>
                    <p className="font-manrope text-white">
                      {format(formData.preferred_date, 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="font-manrope text-neutral-400">
                      {formatTime(formData.preferred_time)}
                    </p>
                  </div>
                )}
              </div>
            )}

            <textarea
              placeholder="Additional Message (Optional)"
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors resize-none"
            />

            <button
              type="submit"
              disabled={loading || (selectedOption === 'calendar' && (!formData.preferred_date || !formData.preferred_time))}
              className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Confirming...
                </>
              ) : (
                "Confirm Booking"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const ContactModal = ({ isOpen, onClose }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const contactOptions = [
    {
      id: "text",
      icon: MessageSquare,
      title: "Text",
      description: "Send us a message"
    },
    {
      id: "call",
      icon: Phone,
      title: "Call",
      description: "Speak with us directly"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/contact/${selectedOption}`, {
        ...formData,
        contact_method: selectedOption
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSelectedOption(null);
        setFormData({ name: "", phone: "", message: "" });
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedOption(null);
    setError(null);
  };

  return (
    <div 
      data-testid="contact-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        data-testid="contact-modal"
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 max-w-md w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {selectedOption ? (
            <button onClick={handleBack} className="text-neutral-400 hover:text-white transition-colors">
              <ChevronLeft size={24} />
            </button>
          ) : (
            <h2 className="font-playfair text-2xl text-white">Contact <span className="text-gold">Us</span></h2>
          )}
          <button 
            data-testid="contact-modal-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-playfair text-xl text-white mb-2">Request Submitted!</h3>
            <p className="font-manrope text-neutral-400 text-sm">We'll be in touch shortly.</p>
          </div>
        ) : !selectedOption ? (
          /* Contact Options */
          <div className="flex flex-col gap-4">
            {contactOptions.map((option) => (
              <button
                key={option.id}
                data-testid={`contact-option-${option.id}`}
                onClick={() => setSelectedOption(option.id)}
                className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-gold/50 hover:bg-gold/5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <option.icon size={22} className="text-gold" />
                </div>
                <div className="text-left">
                  <h3 className="font-manrope text-white font-medium">{option.title}</h3>
                  <p className="font-manrope text-neutral-400 text-sm">{option.description}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          /* Contact Form */
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <h3 className="font-playfair text-xl text-white mb-2">
              Contact by {selectedOption === "text" ? "Text" : "Call"}
            </h3>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <input
              type="text"
              placeholder="Your Name *"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
            />

            <input
              type="tel"
              placeholder="Phone Number *"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: formatPhoneInput(e.target.value) })}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
            />

            <textarea
              placeholder="Your Message (Optional)"
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors resize-none"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Terms & Conditions Modal
const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      data-testid="terms-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        data-testid="terms-modal"
        className="bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="font-playfair text-2xl text-white">Terms & Conditions</h2>
          <button
            data-testid="terms-modal-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="font-manrope text-neutral-300 text-sm space-y-4">
            <p className="text-gold font-semibold">Last Updated: February 2025</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">1. Appointment Policy</h3>
            <p>All appointments at The Tilted Mirror require confirmation. We recommend booking in advance to secure your preferred time slot with Amber Sky Duke.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">2. Cancellation Policy</h3>
            <p>We require at least 24 hours notice for cancellations or rescheduling. Late cancellations or no-shows may be subject to a cancellation fee of up to 50% of the service cost.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">3. Service Pricing</h3>
            <p>Prices may vary based on hair length, thickness, and condition. A consultation may be required for color correction, extensions, or other specialized services. Final pricing will be confirmed during your consultation.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">4. Payment</h3>
            <p>We accept cash, credit cards, and major digital payment methods. Payment is due at the time of service unless otherwise arranged.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">5. Satisfaction Guarantee</h3>
            <p>Your satisfaction is our priority. If you are not completely happy with your service, please contact us within 7 days and we will work to make it right.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">6. Health & Safety</h3>
            <p>Please inform us of any allergies, sensitivities, or medical conditions that may affect your service. We reserve the right to refuse service if we believe it may compromise your health or safety.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">7. Liability</h3>
            <p>The Tilted Mirror is not responsible for any damage to personal belongings. We recommend leaving valuables at home or secured during your appointment.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">8. Contact</h3>
            <p>For questions about these terms, please contact us through the website or visit us at our San Diego location.</p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gold text-black font-manrope font-semibold rounded-lg hover:bg-gold/90 transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Modal
const PrivacyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      data-testid="privacy-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        data-testid="privacy-modal"
        className="bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="font-playfair text-2xl text-white">Privacy Policy</h2>
          <button
            data-testid="privacy-modal-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="font-manrope text-neutral-300 text-sm space-y-4">
            <p className="text-gold font-semibold">Last Updated: February 2025</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">1. Information We Collect</h3>
            <p>We collect information you provide when booking appointments, including your name, phone number, email address, and service preferences. We may also collect information about your hair type and history to better serve you.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">2. How We Use Your Information</h3>
            <p>Your information is used to:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Schedule and manage your appointments</li>
              <li>Send appointment reminders and confirmations</li>
              <li>Provide personalized service recommendations</li>
              <li>Communicate about promotions and updates (with your consent)</li>
              <li>Improve our services</li>
            </ul>
            
            <h3 className="text-white text-lg font-semibold mt-6">3. Information Sharing</h3>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share information with service providers who assist in our operations, subject to confidentiality agreements.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">4. Data Security</h3>
            <p>We implement appropriate security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">5. Your Rights</h3>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
            
            <h3 className="text-white text-lg font-semibold mt-6">6. Cookies & Analytics</h3>
            <p>Our website may use cookies and similar technologies to enhance your browsing experience and analyze site traffic. You can control cookie settings through your browser.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">7. AI Hair Consultant</h3>
            <p>Our AI Hair Consultant may collect conversation data to help with appointment booking and inquiries. This data is used solely to improve our service and is not shared with third parties.</p>
            
            <h3 className="text-white text-lg font-semibold mt-6">8. Contact Us</h3>
            <p>For privacy-related questions or to exercise your rights, please contact us through the website or visit our San Diego location.</p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gold text-black font-manrope font-semibold rounded-lg hover:bg-gold/90 transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

const Navigation = ({ onBookClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = ["Home", "About", "Services", "Reviews", "Gallery"];

  const handleNavClick = (link) => {
    if (link === "Products") {
      navigate('/shop');
    } else if (link === "Home") {
      if (location.pathname !== '/') {
        navigate('/');
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(link.toLowerCase())?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        document.getElementById(link.toLowerCase())?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button onClick={handleLogoClick} data-testid="logo" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_sections-website/artifacts/ejala1g5_LOGO%201%20TRANSPARENT%20WHITE.png" 
              alt="The Tilted Mirror" 
              className="h-10 md:h-14 w-auto object-contain transition-all duration-300"
              style={{ 
                filter: scrolled 
                  ? 'invert(69%) sepia(69%) saturate(398%) hue-rotate(2deg) brightness(94%) contrast(89%)' 
                  : 'none' 
              }}
            />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link}
                onClick={() => handleNavClick(link)}
                data-testid={`nav-${link.toLowerCase()}`}
                className={`text-sm font-manrope tracking-wider transition-colors duration-300 ${
                  scrolled 
                    ? "text-gold hover:text-white" 
                    : "text-neutral-300 hover:text-gold"
                }`}
              >
                {link}
              </button>
            ))}
          </div>

          {/* CTA Button */}
          <button
            data-testid="book-seat-btn"
            onClick={onBookClick}
            className={`hidden md:block px-6 py-2.5 rounded-full text-sm font-manrope tracking-widest uppercase transition-all duration-300 ${
              scrolled 
                ? "bg-transparent border border-gold text-gold hover:bg-gold hover:text-black" 
                : "bg-transparent border border-white text-white hover:bg-white hover:text-black"
            }`}
          >
            Book a Seat
          </button>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-md border-t border-white/10 shadow-2xl">
            <div className="flex flex-col gap-3 px-6 py-6">
              {navLinks.map((link) => (
                <button
                  key={link}
                  onClick={() => handleNavClick(link)}
                  className="text-base font-manrope tracking-wider text-white hover:text-gold transition-colors duration-300 text-left py-2"
                >
                  {link}
                </button>
              ))}
              <button 
                onClick={() => { setMobileMenuOpen(false); onBookClick(); }}
                className="mt-3 px-4 py-2 rounded-full bg-gold text-black text-xs font-manrope tracking-widest uppercase w-fit"
              >
                Book a Seat
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-start"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1920&q=80"
          alt="Hair styling"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-20 flex flex-col items-center justify-center min-h-screen text-center">
        <div>
          <p className="font-caveat text-gold text-2xl md:text-3xl mb-4 animate-fade-in">
            welcome to
          </p>
          <h1 className="font-playfair text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white leading-tight mb-6 animate-slide-up">
            THE TILTED <span className="text-gold">MIRROR</span>
          </h1>
          <p className="font-manrope text-neutral-300 text-sm md:text-base tracking-wider mb-8 animate-fade-in-delay text-center italic max-w-2xl mx-auto">
            "I think that the most important thing a woman can have - next to talent, of course - is her hairdresser"
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          data-testid="scroll-down-btn"
          className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            const aboutSection = document.getElementById('about');
            if (aboutSection) {
              aboutSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          <ChevronDown size={20} />
        </button>
      </div>
    </section>
  );
};

const WelcomeSection = ({ onAmberClick }) => {
  return (
    <section
      id="about"
      data-testid="welcome-section"
      className="py-16 md:py-24 lg:py-32 bg-dark"
    >
      <div className="max-w-4xl mx-auto px-4 md:px-12 lg:px-24 text-center">
        {/* Logo */}
        <img 
          src="/logo-transparent.png" 
          alt="The Tilted Mirror" 
          className="w-32 md:w-40 lg:w-48 mx-auto mb-8 md:mb-12"
        />
        
        <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-2 md:mb-4">
          <span className="text-gold italic">WELCOME</span> TO OUR
        </h2>
        <h3 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-8 md:mb-12">
          HAIR ATELIER STUDIO
        </h3>
        
        <p className="font-manrope text-neutral-400 text-sm md:text-base leading-relaxed tracking-wide max-w-2xl mx-auto px-2 mb-8">
          At The Tilted Mirror, we deliver a refined salon experience centered on quality, comfort, and personalized service.
        </p>

        {/* About Amber Button */}
        <button
          data-testid="welcome-amber-btn"
          onClick={onAmberClick}
          className="inline-flex items-center gap-2 px-6 py-3 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-all duration-300 font-manrope text-sm tracking-wider"
        >
          <Sparkles size={18} />
          About Amber
        </button>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback to default services if API fails
        setServices([
          { id: '1', name: "CUTTING", image_url: "https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&q=80", is_featured: true },
          { id: '2', name: "BRAIDS", image_url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=80", is_featured: true },
          { id: '3', name: "COLOUR", image_url: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?w=600&q=80", is_featured: true },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Filter to show only featured services, or show all if none are featured
  const featuredServices = services.filter(s => s.is_featured);
  const displayServices = featuredServices.length > 0 ? featuredServices : (services.length > 0 ? services.slice(0, 3) : [
    { id: '1', name: "CUTTING", image_url: "https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&q=80" },
    { id: '2', name: "BRAIDS", image_url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=80" },
    { id: '3', name: "COLOUR", image_url: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?w=600&q=80" },
  ]);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-16 md:py-24 lg:py-32 bg-dark"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
        {/* Header - Centered */}
        <div className="text-center mb-8 md:mb-12">
          <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">Our</p>
          <h2 className="font-playfair text-3xl md:text-4xl text-white">SERVICES</h2>
        </div>

        {/* Services Carousel */}
        <div className="relative">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Prev Button */}
            <button
              data-testid="services-prev-btn"
              className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 flex items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Services Grid */}
            <div className="flex-1 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {displayServices.slice(0, 3).map((service) => (
                  <div
                    key={service.id}
                    data-testid={`service-card-${service.id}`}
                    onClick={() => navigate('/services')}
                    className="group relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer"
                  >
                    <img
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-center">
                      <h3 className="font-playfair text-lg md:text-xl text-white mb-2 md:mb-3">{service.name}</h3>
                      <button className="inline-flex items-center gap-2 text-sm font-manrope tracking-wider text-white/80 hover:text-gold transition-colors duration-300">
                        More
                        <span className="w-6 h-6 rounded-full border border-white/30 flex items-center justify-center group-hover:border-gold transition-colors duration-300">
                          <ChevronRight size={12} />
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <button
              data-testid="services-next-btn"
              className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 flex items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const BrandsSection = () => {
  const brands = [
    {
      name: "Davines",
      logo: "https://customer-assets.emergentagent.com/job_04d26f3f-02b5-40ab-adcf-e4d4bc1b1e71/artifacts/mvma1weq_davines-svg-logo_mobile.svg"
    },
    {
      name: "Oway",
      logo: "https://customer-assets.emergentagent.com/job_04d26f3f-02b5-40ab-adcf-e4d4bc1b1e71/artifacts/r74xdxok_Logo_-_Rectangle_copy.avif"
    },
    {
      name: "Pura Luxe",
      logo: "https://customer-assets.emergentagent.com/job_04d26f3f-02b5-40ab-adcf-e4d4bc1b1e71/artifacts/baw23pr2_PuraLuxe_BrandBook_pura_luxe_420x.avif"
    }
  ];

  return (
    <section
      id="products"
      data-testid="brands-section"
      className="py-16 md:py-24 lg:py-32 bg-dark"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 text-center">
        {/* Decorative Line */}
        <div className="w-8 h-0.5 bg-gold mx-auto mb-6 md:mb-8"></div>
        
        <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">we use</p>
        <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl text-white mb-10 md:mb-16">ONLY THE BEST</h2>

        {/* Brands */}
        <div className="border-y border-white/10 py-8 md:py-10">
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16 lg:gap-24">
            {brands.map((brand, index) => (
              <div
                key={index}
                data-testid={`brand-${index}`}
                className="flex items-center justify-center h-6 md:h-8"
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="h-full w-auto object-contain brightness-0 invert opacity-60 hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewsSection = () => {
  const [currentReview, setCurrentReview] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback reviews in case API returns empty or fails
  const fallbackReviews = [
    {
      id: "fallback-1",
      text: "I can't express how much The Tilted Mirror has transformed my hair! After struggling with tangly hair for years, I finally found great results from their keratin treatment. My hair is now shiny and full of volume. It doesn't get overly tangled like it used to before. I'm forever grateful!",
      author: "R. David Edelman",
      rating: 5,
      avatar_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80",
    },
    {
      id: "fallback-2",
      text: "The best salon experience I've ever had. The attention to detail and the luxurious atmosphere made me feel like royalty. My color has never looked better!",
      author: "Sarah Mitchell",
      rating: 5,
      avatar_url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80",
    },
    {
      id: "fallback-3",
      text: "Absolutely amazing service from start to finish. The stylists really listen to what you want and deliver beyond expectations. Highly recommend!",
      author: "Michael Chen",
      rating: 5,
      avatar_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80",
    },
  ];

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        // Filter only featured/active reviews for homepage
        const featuredReviews = response.data.filter(r => r.is_featured && r.is_active);
        setReviews(featuredReviews.length > 0 ? featuredReviews : fallbackReviews);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews(fallbackReviews);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const displayReviews = reviews.length > 0 ? reviews : fallbackReviews;

  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % displayReviews.length);
  };

  const prevReview = () => {
    setCurrentReview((prev) => (prev - 1 + displayReviews.length) % displayReviews.length);
  };

  // Handle case when currentReview index is out of bounds
  const safeCurrentReview = currentReview >= displayReviews.length ? 0 : currentReview;
  const currentReviewData = displayReviews[safeCurrentReview];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-16 md:py-24 lg:py-32 bg-secondary"
    >
      <div className="max-w-5xl mx-auto px-4 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="w-8 h-0.5 bg-gold mx-auto mb-6 md:mb-8"></div>
          <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">client</p>
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl text-white">REVIEWS</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={32} className="text-gold animate-spin" />
          </div>
        ) : (
          /* Review Carousel */
          <div className="relative">
            <div className="flex items-center justify-center gap-4 md:gap-8">
              {/* Prev Button */}
              <button
                data-testid="reviews-prev-btn"
                onClick={prevReview}
                className="hidden md:flex w-12 h-12 rounded-full border border-white/30 items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Review Content */}
              <div className="flex-1 max-w-2xl text-center px-2">
                {/* Stars */}
                <div className="flex items-center justify-center gap-1 mb-4 md:mb-6">
                  {[...Array(currentReviewData.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-500 text-yellow-500" />
                  ))}
                  <span className="ml-2 text-sm font-manrope text-neutral-400">
                    {currentReviewData.rating}.0
                  </span>
                </div>

                {/* Quote */}
                <p className="font-manrope text-neutral-300 text-sm md:text-base leading-relaxed mb-6 md:mb-8 italic">
                  "{currentReviewData.text}"
                </p>

                {/* Author */}
                <div className="flex flex-col items-center">
                  {currentReviewData.avatar_url ? (
                    <img
                      src={currentReviewData.avatar_url}
                      alt={currentReviewData.author}
                      className="w-12 h-12 rounded-full object-cover mb-3 border-2 border-gold/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mb-3 border-2 border-gold/30">
                      <span className="text-gold font-playfair text-lg">{currentReviewData.author.charAt(0)}</span>
                    </div>
                  )}
                  <p className="font-manrope text-white text-sm tracking-wider">
                    {currentReviewData.author}
                  </p>
                </div>
              </div>

              {/* Next Button */}
              <button
                data-testid="reviews-next-btn"
                onClick={nextReview}
                className="hidden md:flex w-12 h-12 rounded-full border border-white/30 items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Mobile Navigation */}
            <div className="flex md:hidden justify-center gap-4 mt-8">
              <button
                onClick={prevReview}
                className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white/50"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={nextReview}
                className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white/50"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {displayReviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentReview(index)}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    index === safeCurrentReview ? "bg-gold" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const GallerySection = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const defaultImages = [
    { id: '1', image_url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=80", title: "Blonde wavy hair", is_featured: true },
    { id: '2', image_url: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&q=80", title: "Red curly hair", is_featured: true },
    { id: '3', image_url: "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=600&q=80", title: "Braided hair", is_featured: true },
    { id: '4', image_url: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?w=600&q=80", title: "Colorful hair", is_featured: true },
    { id: '5', image_url: "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=600&q=80", title: "Styled hair", is_featured: true },
  ];

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryItems(response.data.length > 0 ? response.data : defaultImages);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setGalleryItems(defaultImages);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Filter to show only featured gallery items, or show all if none are featured
  const featuredGallery = galleryItems.filter(item => item.is_featured);
  const galleryImages = featuredGallery.length > 0 ? featuredGallery : (galleryItems.length > 0 ? galleryItems : defaultImages);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Get visible images (3 on desktop, 1 on mobile)
  const getVisibleImages = (count) => {
    const images = [];
    for (let i = 0; i < count; i++) {
      const index = (currentIndex + i) % galleryImages.length;
      images.push(galleryImages[index]);
    }
    return images;
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-16 md:py-24 lg:py-32 bg-[#0d1117]"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">take a look at</p>
          <h2 className="font-playfair text-3xl md:text-4xl text-white">OUR WORK</h2>
        </div>

        {/* Gallery Carousel */}
        <div className="relative">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Prev Button */}
            <button
              data-testid="gallery-prev-btn"
              onClick={prevSlide}
              className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 flex items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Images Grid - 1 on mobile, 3 on desktop */}
            <div className="flex-1 overflow-hidden">
              {/* Mobile: Show 1 image */}
              <div className="block md:hidden">
                <div className="grid grid-cols-1 gap-4">
                  {getVisibleImages(1).map((image, index) => (
                    <div
                      key={`mobile-${image.id}-${index}`}
                      data-testid={`gallery-image-mobile-${index}`}
                      onClick={() => navigate('/gallery')}
                      className="group relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer"
                    >
                      <img
                        src={image.image_url}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      
                      {/* More Button */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                        <button className="inline-flex items-center gap-2 text-sm font-manrope tracking-wider text-white/80 hover:text-gold transition-colors duration-300">
                          More
                          <span className="w-6 h-6 rounded-full border border-white/30 flex items-center justify-center group-hover:border-gold transition-colors duration-300">
                            <ChevronRight size={12} />
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Desktop: Show 3 images */}
              <div className="hidden md:block">
                <div className="grid grid-cols-3 gap-6">
                  {getVisibleImages(3).map((image, index) => (
                    <div
                      key={`desktop-${image.id}-${index}`}
                      data-testid={`gallery-image-${index}`}
                      onClick={() => navigate('/gallery')}
                      className="group relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer"
                    >
                      <img
                        src={image.image_url}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      
                      {/* More Button */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-center">
                        <button className="inline-flex items-center gap-2 text-sm font-manrope tracking-wider text-white/80 hover:text-gold transition-colors duration-300">
                          More
                          <span className="w-6 h-6 rounded-full border border-white/30 flex items-center justify-center group-hover:border-gold transition-colors duration-300">
                            <ChevronRight size={12} />
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Next Button */}
            <button
              data-testid="gallery-next-btn"
              onClick={nextSlide}
              className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 flex items-center justify-center text-white/50 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  index === currentIndex ? "bg-gold" : "bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ onContactClick, onTermsClick, onPrivacyClick }) => {
  const navigate = useNavigate();
  
  const footerLinks = {
    column1: ["Home", "About", "Services"],
    column2: ["Reviews", "Gallery"],
    column3: ["Terms and Conditions", "Privacy Policy"]
  };

  const handleLinkClick = (e, link) => {
    if (link === "Terms and Conditions") {
      e.preventDefault();
      onTermsClick?.();
    } else if (link === "Privacy Policy") {
      e.preventDefault();
      onPrivacyClick?.();
    } else if (link === "Products") {
      e.preventDefault();
      navigate('/shop');
    }
  };

  return (
    <footer data-testid="footer" className="bg-dark border-t border-white/10">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-10 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Logo & Description */}
          <div className="sm:col-span-2 lg:col-span-1">
            <a href="#" className="flex items-center mb-4 md:mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_sections-website/artifacts/ejala1g5_LOGO%201%20TRANSPARENT%20WHITE.png" 
                alt="The Tilted Mirror" 
                className="h-10 md:h-12 w-auto object-contain"
                style={{ filter: 'invert(69%) sepia(69%) saturate(398%) hue-rotate(2deg) brightness(94%) contrast(89%)' }}
              />
            </a>
            <p className="font-manrope text-neutral-400 text-sm leading-relaxed">
              We are not just a salon. We are a sanctuary for your style. Experience the art of hair.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="grid grid-cols-3 gap-4 md:gap-8">
              {Object.values(footerLinks).map((links, colIndex) => (
                <div key={colIndex} className="flex flex-col gap-2 md:gap-3">
                  {links.map((link) => (
                    <a
                      key={link}
                      href={`#${link.toLowerCase().replace(/\s+/g, '-')}`}
                      data-testid={`footer-link-${link.toLowerCase().replace(/\s+/g, '-')}`}
                      className="font-manrope text-xs sm:text-sm text-neutral-400 hover:text-gold transition-colors duration-300 cursor-pointer"
                      onClick={(e) => handleLinkClick(e, link)}
                    >
                      {link}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Us Button & Social */}
          <div className="sm:col-span-2 lg:col-span-1">
            {/* Contact Us Button */}
            <button
              data-testid="footer-contact-btn"
              onClick={onContactClick}
              className="px-5 md:px-6 py-2 md:py-2.5 rounded-full bg-transparent border border-gold text-gold text-xs md:text-sm font-manrope tracking-widest uppercase hover:bg-gold hover:text-black transition-all duration-300 mb-4 md:mb-6"
            >
              Contact Us
            </button>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="#"
                data-testid="social-instagram"
                className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-gold/20 hover:text-gold transition-all duration-300"
              >
                <Instagram size={16} />
              </a>
              <a
                href="#"
                data-testid="social-facebook"
                className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-gold/20 hover:text-gold transition-all duration-300"
              >
                <Facebook size={16} />
              </a>
              <a
                href="#"
                data-testid="social-twitter"
                className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-gold/20 hover:text-gold transition-all duration-300"
              >
                <Twitter size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-4 md:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4">
            <p className="font-manrope text-neutral-500 text-xs">
              © 2024 The Tilted Mirror | All rights reserved
            </p>
            <div className="flex items-center gap-4">
              <button
                data-testid="footer-admin-link"
                onClick={() => navigate('/admin')}
                className="font-manrope text-neutral-500 text-xs hover:text-gold transition-colors"
              >
                Admin
              </button>
              <p className="font-manrope text-neutral-500 text-xs">
                Web Design by <a href="https://unizar.ai" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Unizar</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============ SHOP COMPONENTS ============

const ShopPage = ({ cart, setCart, onBookClick }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [searchParams] = useSearchParams();
  const [orderSuccess, setOrderSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    
    // Check for success/cancel params
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setOrderSuccess(true);
        setCart([]);
        // Clear URL params
        navigate('/shop', { replace: true });
      } else if (response.data.status === 'expired') {
        navigate('/shop', { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product_id: product.id, quantity: 1, product }];
    });
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-dark pt-24">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-green-500" />
          </div>
          <h1 className="font-playfair text-3xl md:text-4xl text-white mb-4">Order Confirmed!</h1>
          <p className="font-manrope text-neutral-400 mb-8">
            Thank you for your purchase. You will receive an email confirmation shortly.
          </p>
          <button
            onClick={() => { setOrderSuccess(false); navigate('/shop'); }}
            className="px-8 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">shop our</p>
            <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-white">PRODUCTS</h1>
          </div>
          <button
            data-testid="cart-btn"
            onClick={() => setShowCart(true)}
            className="relative px-6 py-3 rounded-full bg-transparent border border-gold text-gold font-manrope tracking-wider uppercase hover:bg-gold hover:text-black transition-all duration-300 flex items-center gap-2"
          >
            <ShoppingCart size={18} />
            Cart
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gold text-black text-xs font-bold flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={40} className="text-gold animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-${product.id}`}
                className="bg-white/5 border border-white/10 rounded-xl overflow-hidden group hover:border-gold/30 transition-all duration-300"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h3 className="font-playfair text-lg text-white mb-2">{product.name}</h3>
                  <p className="font-manrope text-neutral-400 text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-manrope text-gold text-xl font-semibold">${product.price.toFixed(2)}</span>
                    <button
                      data-testid={`add-to-cart-${product.id}`}
                      onClick={() => addToCart(product)}
                      className="px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-manrope tracking-wider uppercase hover:bg-gold hover:text-black transition-all duration-300"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Back to Home */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/')}
            className="font-manrope text-neutral-400 hover:text-gold transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <CartSidebar
          cart={cart}
          setCart={setCart}
          onClose={() => setShowCart(false)}
          onCheckout={() => { setShowCart(false); setShowCheckout(true); }}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          cart={cart}
          onClose={() => setShowCheckout(false)}
          onSuccess={() => setOrderSuccess(true)}
        />
      )}
    </div>
  );
};

const CartSidebar = ({ cart, setCart, onClose, onCheckout }) => {
  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeItem = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const shipping = 10.00;
  const total = subtotal + shipping;

  return (
    <div className="fixed inset-0 z-[100]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="absolute right-0 top-0 h-full w-full max-w-md bg-[#1a1a1a] border-l border-white/10 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-playfair text-2xl text-white">Your <span className="text-gold">Cart</span></h2>
            <button
              data-testid="close-cart"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
            >
              <X size={18} />
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={48} className="text-neutral-600 mx-auto mb-4" />
              <p className="font-manrope text-neutral-400">Your cart is empty</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4 mb-6">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex gap-4 p-3 bg-white/5 rounded-lg">
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="font-manrope text-white text-sm mb-1">{item.product.name}</h4>
                      <p className="font-manrope text-gold text-sm mb-2">${item.product.price.toFixed(2)}</p>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`cart-decrease-${item.product_id}`}
                          onClick={() => updateQuantity(item.product_id, -1)}
                          className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white hover:bg-gold/20"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="font-manrope text-white text-sm w-8 text-center">{item.quantity}</span>
                        <button
                          data-testid={`cart-increase-${item.product_id}`}
                          onClick={() => updateQuantity(item.product_id, 1)}
                          className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white hover:bg-gold/20"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          data-testid={`cart-remove-${item.product_id}`}
                          onClick={() => removeItem(item.product_id)}
                          className="ml-auto text-neutral-500 hover:text-red-400"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 mb-6">
                <div className="flex justify-between mb-2">
                  <span className="font-manrope text-neutral-400">Subtotal</span>
                  <span className="font-manrope text-white">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="font-manrope text-neutral-400">Shipping</span>
                  <span className="font-manrope text-white">${shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span className="font-manrope text-white font-medium">Total</span>
                  <span className="font-manrope text-gold font-semibold">${total.toFixed(2)}</span>
                </div>
              </div>

              <button
                data-testid="checkout-btn"
                onClick={onCheckout}
                className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all duration-300"
              >
                Proceed to Checkout
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CheckoutModal = ({ cart, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subtotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const shipping = 10.00;
  const total = subtotal + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/checkout`, {
        items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: formData.shipping_address,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl text-white">Checkout</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Full Name *"
            required
            value={formData.customer_name}
            onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
          />
          <input
            type="email"
            placeholder="Email Address *"
            required
            value={formData.customer_email}
            onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
          />
          <input
            type="tel"
            placeholder="Phone Number"
            value={formData.customer_phone}
            onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
          />
          <textarea
            placeholder="Shipping Address *"
            required
            rows={3}
            value={formData.shipping_address}
            onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors resize-none"
          />

          <div className="border-t border-white/10 pt-4 mt-2">
            <div className="flex justify-between mb-2">
              <span className="font-manrope text-neutral-400">Subtotal</span>
              <span className="font-manrope text-white">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-manrope text-neutral-400">Shipping (Flat Rate)</span>
              <span className="font-manrope text-white">${shipping.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg mb-4">
              <span className="font-manrope text-white font-medium">Total</span>
              <span className="font-manrope text-gold font-semibold">${total.toFixed(2)}</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Processing...
              </>
            ) : (
              "Pay with Stripe"
            )}
          </button>
          
          <p className="text-center text-neutral-500 text-xs">
            Secure payment powered by Stripe
          </p>
        </form>
      </div>
    </div>
  );
};

// ============ ADMIN COMPONENTS ============

const AdminLoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminTokenExpires', response.data.expires_at);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center px-4">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl text-white mb-2">Admin <span className="text-gold">Login</span></h1>
          <p className="font-manrope text-neutral-400 text-sm">Enter your credentials to access the admin panel</p>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            data-testid="admin-username"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
          />
          <input
            type="password"
            placeholder="Password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Logging in...
              </>
            ) : (
              "Login"
            )}
          </button>
        </form>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="font-manrope text-neutral-400 text-sm hover:text-gold transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Appointment Card Component with status management and propose date/time
const AppointmentCard = ({ appointment, onUpdate, services = [] }) => {
  const [showPropose, setShowPropose] = useState(false);
  const [proposedDate, setProposedDate] = useState(appointment.proposed_date || '');
  const [proposedTime, setProposedTime] = useState(appointment.proposed_time || '');
  const [loading, setLoading] = useState(false);

  // Get duration - either from appointment or look up from services
  const getDuration = () => {
    if (appointment.duration_minutes) return appointment.duration_minutes;
    if (appointment.service_type && services.length > 0) {
      const service = services.find(s => s.name.toLowerCase() === appointment.service_type.toLowerCase());
      if (service) return service.duration_minutes || 60;
    }
    return null;
  };
  const duration = getDuration();

  const updateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/admin/appointments/${appointment.id}/status?status=${newStatus}`);
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const proposeDateTime = async () => {
    if (!proposedDate || !proposedTime) return;
    setLoading(true);
    try {
      await axios.patch(`${API}/admin/appointments/${appointment.id}/propose`, {
        proposed_date: proposedDate,
        proposed_time: proposedTime
      });
      setShowPropose(false);
      onUpdate();
    } catch (error) {
      console.error('Error proposing date/time:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
    completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            appointment.booking_type === 'calendar' ? 'bg-blue-500/20' :
            appointment.booking_type === 'chat' ? 'bg-gold/20' :
            appointment.booking_type === 'text' ? 'bg-green-500/20' : 'bg-purple-500/20'
          }`}>
            {appointment.booking_type === 'calendar' && <Calendar size={20} className="text-blue-400" />}
            {appointment.booking_type === 'chat' && <MessageCircle size={20} className="text-gold" />}
            {appointment.booking_type === 'text' && <MessageSquare size={20} className="text-green-400" />}
            {appointment.booking_type === 'call' && <PhoneCall size={20} className="text-purple-400" />}
          </div>
          <div>
            <h3 className="font-manrope text-white font-medium">{appointment.name}</h3>
            <div className="flex items-center gap-3 flex-wrap">
              {appointment.phone && (
                <p className="font-manrope text-gold text-sm flex items-center gap-1">
                  <Phone size={12} />
                  {formatPhoneNumber(appointment.phone)}
                </p>
              )}
              {appointment.email && (
                <p className="font-manrope text-neutral-400 text-sm">
                  {appointment.email}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-manrope capitalize ${
            appointment.booking_type === 'calendar' ? 'bg-blue-500/20 text-blue-400' :
            appointment.booking_type === 'chat' ? 'bg-gold/20 text-gold' :
            appointment.booking_type === 'text' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'
          }`}>
            {appointment.booking_type}
          </span>
          <select
            value={appointment.status}
            onChange={(e) => updateStatus(e.target.value)}
            className={`px-3 py-1.5 rounded-lg text-sm border cursor-pointer ${statusColors[appointment.status] || 'bg-white/10 text-white border-white/20'}`}
            data-testid={`appointment-status-${appointment.id}`}
          >
            <option value="pending" className="bg-[#1a1a1a] text-white">Pending</option>
            <option value="confirmed" className="bg-[#1a1a1a] text-white">Confirmed</option>
            <option value="completed" className="bg-[#1a1a1a] text-white">Completed</option>
            <option value="cancelled" className="bg-[#1a1a1a] text-white">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 border-t border-white/10 pt-4">
        {appointment.service_type && (
          <div>
            <p className="font-manrope text-neutral-500 text-xs mb-1">Service</p>
            <div className="flex items-center gap-2">
              <p className="font-manrope text-white text-sm capitalize">{appointment.service_type}</p>
              {duration && (
                <span className="font-manrope text-neutral-400 text-xs flex items-center gap-1">
                  <Clock size={10} />
                  {(() => {
                    const mins = duration;
                    if (mins >= 60) {
                      const hrs = Math.floor(mins / 60);
                      const remainingMins = mins % 60;
                      return remainingMins > 0 ? `${hrs}hr ${remainingMins}min` : `${hrs}hr${hrs > 1 ? 's' : ''}`;
                    }
                    return `${mins}min`;
                  })()}
                </span>
              )}
            </div>
          </div>
        )}
        {appointment.preferred_date && (
          <div>
            <p className="font-manrope text-neutral-500 text-xs mb-1">Preferred Date</p>
            <p className="font-manrope text-white text-sm">{formatDate(appointment.preferred_date)}</p>
          </div>
        )}
        {appointment.preferred_time && (
          <div>
            <p className="font-manrope text-neutral-500 text-xs mb-1">Preferred Time</p>
            <p className="font-manrope text-white text-sm">
              {formatTime(appointment.preferred_time)}
              {duration && (
                <span className="text-neutral-400 text-xs ml-1">
                  - {(() => {
                    const [h, m] = appointment.preferred_time.split(':').map(Number);
                    const endMins = h * 60 + m + duration;
                    const endH = Math.floor(endMins / 60);
                    const endM = endMins % 60;
                    const hour12 = endH > 12 ? endH - 12 : endH === 0 ? 12 : endH;
                    const ampm = endH >= 12 ? 'PM' : 'AM';
                    return `${hour12}:${endM.toString().padStart(2, '0')} ${ampm}`;
                  })()}
                </span>
              )}
            </p>
          </div>
        )}
        {appointment.message && (
          <div className="md:col-span-3">
            <p className="font-manrope text-neutral-500 text-xs mb-1">Message</p>
            <p className="font-manrope text-white text-sm">{appointment.message}</p>
          </div>
        )}
      </div>

      {/* Proposed Date/Time Section */}
      {appointment.has_proposal && appointment.proposed_date && (
        <div className="mt-4 p-3 bg-gold/10 border border-gold/30 rounded-lg">
          <p className="font-manrope text-gold text-xs mb-2 font-medium">Proposed New Date/Time:</p>
          <div className="flex gap-4">
            <p className="font-manrope text-white text-sm">
              <Clock size={14} className="inline mr-1 text-gold" />
              {formatDate(appointment.proposed_date)} at {formatTime(appointment.proposed_time)}
            </p>
          </div>
        </div>
      )}

      {/* Propose Date/Time Button & Form */}
      <div className="mt-4 pt-4 border-t border-white/10">
        {!showPropose ? (
          <div className="flex items-center justify-between">
            <p className="font-manrope text-neutral-500 text-xs">
              Created: {formatDateTime(appointment.created_at)}
            </p>
            <button
              onClick={() => setShowPropose(true)}
              className="px-4 py-2 rounded-lg bg-gold/20 text-gold text-sm font-manrope hover:bg-gold/30 transition-all flex items-center gap-2"
              data-testid={`propose-btn-${appointment.id}`}
            >
              <Clock size={14} />
              Propose New Date/Time
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="font-manrope text-gold text-sm font-medium">Propose New Date & Time</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-manrope text-neutral-400 text-xs mb-1 block">Date</label>
                <input
                  type="date"
                  value={proposedDate}
                  onChange={(e) => setProposedDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:border-gold/50 focus:outline-none"
                  data-testid={`propose-date-${appointment.id}`}
                />
              </div>
              <div>
                <label className="font-manrope text-neutral-400 text-xs mb-1 block">Time</label>
                <input
                  type="time"
                  value={proposedTime}
                  onChange={(e) => setProposedTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:border-gold/50 focus:outline-none"
                  data-testid={`propose-time-${appointment.id}`}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={proposeDateTime}
                disabled={loading || !proposedDate || !proposedTime}
                className="px-4 py-2 rounded-lg bg-gold text-black text-sm font-manrope hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid={`save-proposal-${appointment.id}`}
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Save Proposal
              </button>
              <button
                onClick={() => setShowPropose(false)}
                className="px-4 py-2 rounded-lg bg-white/10 text-white text-sm font-manrope hover:bg-white/20 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Appointment Calendar View Component
const AppointmentCalendarView = ({ appointments, onSelectAppointment }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      // Check preferred_date or proposed_date
      const aptDate = apt.proposed_date || apt.preferred_date;
      return aptDate === dateStr;
    });
  };

  // Get dates that have appointments
  const getDatesWithAppointments = () => {
    const dates = [];
    appointments.forEach(apt => {
      const dateStr = apt.proposed_date || apt.preferred_date;
      if (dateStr) {
        dates.push(new Date(dateStr + 'T00:00:00'));
      }
    });
    return dates;
  };

  const appointmentsForSelectedDate = getAppointmentsForDate(selectedDate);
  const datesWithAppointments = getDatesWithAppointments();

  const statusColors = {
    pending: 'bg-yellow-500',
    confirmed: 'bg-green-500',
    completed: 'bg-blue-500',
    cancelled: 'bg-red-500'
  };

  const statusBgColors = {
    pending: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
    confirmed: 'bg-green-500/20 border-green-500/30 text-green-400',
    completed: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    cancelled: 'bg-red-500/20 border-red-500/30 text-red-400'
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-1 bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="font-playfair text-xl text-white mb-4 text-center">Select Date</h3>
        <CalendarComponent
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
          className="rounded-md border-0 mx-auto"
          modifiers={{
            hasAppointment: datesWithAppointments
          }}
          modifiersStyles={{
            hasAppointment: {
              backgroundColor: 'rgba(212, 175, 55, 0.3)',
              borderRadius: '50%'
            }
          }}
          classNames={{
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center text-white",
            caption_label: "text-sm font-medium text-white",
            nav: "space-x-1 flex items-center",
            nav_button: "h-7 w-7 bg-white/10 p-0 opacity-70 hover:opacity-100 rounded-md text-white hover:bg-white/20",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-neutral-400 rounded-md w-9 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 h-9 w-9",
            day: "h-9 w-9 p-0 font-normal text-neutral-300 hover:bg-white/10 rounded-md transition-colors",
            day_selected: "bg-gold text-black hover:bg-gold hover:text-black focus:bg-gold focus:text-black rounded-md",
            day_today: "bg-white/20 text-white rounded-md",
            day_outside: "text-neutral-600 opacity-50",
            day_disabled: "text-neutral-600 opacity-50",
            day_hidden: "invisible",
          }}
        />
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="font-manrope text-neutral-400 text-xs text-center">
            <span className="inline-block w-3 h-3 rounded-full bg-gold/30 mr-2"></span>
            Days with appointments
          </p>
        </div>
      </div>

      {/* Appointments for Selected Date */}
      <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="font-playfair text-xl text-white mb-4">
          {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </h3>
        
        {appointmentsForSelectedDate.length === 0 ? (
          <div className="text-center py-12">
            <CalendarCheck size={48} className="text-neutral-600 mx-auto mb-4" />
            <p className="font-manrope text-neutral-400">No appointments for this date</p>
          </div>
        ) : (
          <div className="space-y-3">
            {appointmentsForSelectedDate.map((apt) => (
              <div 
                key={apt.id}
                onClick={() => {
                  setSelectedAppointment(apt);
                  onSelectAppointment && onSelectAppointment(apt);
                }}
                className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-gold/50 ${
                  selectedAppointment?.id === apt.id ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/5'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-10 rounded-full ${statusColors[apt.status] || 'bg-gray-500'}`}></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-manrope text-white font-medium">{apt.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-manrope border ${statusBgColors[apt.status]}`}>
                          {apt.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {apt.phone && (
                          <p className="font-manrope text-gold text-sm flex items-center gap-1">
                            <Phone size={12} />
                            {apt.phone}
                          </p>
                        )}
                        {apt.email && (
                          <p className="font-manrope text-neutral-400 text-sm">
                            {apt.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-manrope text-gold text-sm font-medium">
                      {formatTime(apt.proposed_time || apt.preferred_time) || 'Time TBD'}
                    </p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-manrope capitalize ${
                      apt.booking_type === 'calendar' ? 'bg-blue-500/20 text-blue-400' :
                      apt.booking_type === 'chat' ? 'bg-gold/20 text-gold' :
                      apt.booking_type === 'text' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {apt.booking_type}
                    </span>
                  </div>
                </div>
                
                {apt.service_type && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-gold" />
                      <span className="font-manrope text-gold text-sm capitalize">{apt.service_type}</span>
                    </div>
                  </div>
                )}
                
                {apt.message && (
                  <div className="mt-2">
                    <p className="font-manrope text-neutral-400 text-xs italic">"{apt.message}"</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Blocked Time Form Component
const BlockedTimeForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    date: null,
    start_time: '',
    end_time: '',
    reason: '',
    is_full_day: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [startTimeOpen, setStartTimeOpen] = useState(false);
  const [endTimeOpen, setEndTimeOpen] = useState(false);

  // Generate time slots from 6 AM to 10 PM in 30-minute intervals
  const timeSlots = [];
  for (let hour = 6; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const time24 = `${h}:${m}`;
      const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayTime = `${hour12}:${m.padStart(2, '0')} ${ampm}`;
      timeSlots.push({ value: time24, label: displayTime });
    }
  }

  const formatTimeDisplay = (time24) => {
    if (!time24) return 'Select time';
    const [hour, minute] = time24.split(':').map(Number);
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date) {
      setError('Please select a date');
      return;
    }
    if (!formData.is_full_day && (!formData.start_time || !formData.end_time)) {
      setError('Please select start and end times');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const dateStr = format(formData.date, 'yyyy-MM-dd');
      await axios.post(`${API}/blocked-times`, {
        date: dateStr,
        start_time: formData.is_full_day ? null : formData.start_time,
        end_time: formData.is_full_day ? null : formData.end_time,
        reason: formData.reason || null,
        is_full_day: formData.is_full_day
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to block time');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>
      )}
      
      {/* Date Picker with Calendar Popup */}
      <div>
        <label className="font-manrope text-neutral-400 text-sm block mb-2">Date *</label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-left flex items-center justify-between hover:border-gold/50 focus:border-gold/50 focus:outline-none transition-colors"
            >
              <span className={formData.date ? 'text-white' : 'text-neutral-500'}>
                {formData.date ? format(formData.date, 'MMMM d, yyyy') : 'Click to select date'}
              </span>
              <Calendar size={18} className="text-neutral-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#1a1a1a] border-white/10" align="start">
            <CalendarComponent
              mode="single"
              selected={formData.date}
              onSelect={(date) => {
                setFormData({ ...formData, date });
                setCalendarOpen(false);
              }}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
              className="rounded-md"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4",
                caption: "flex justify-center pt-1 relative items-center text-white",
                caption_label: "text-sm font-medium text-white",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-white/10 hover:bg-white/20 rounded-md flex items-center justify-center text-white",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-neutral-400 rounded-md w-9 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-gold/20 rounded-md",
                day: "h-9 w-9 p-0 font-normal text-white hover:bg-white/10 rounded-md flex items-center justify-center",
                day_selected: "bg-gold text-black hover:bg-gold hover:text-black focus:bg-gold focus:text-black",
                day_today: "bg-white/10 text-gold",
                day_outside: "text-neutral-600 opacity-50",
                day_disabled: "text-neutral-600 opacity-30",
                day_hidden: "invisible",
              }}
            />
          </PopoverContent>
        </Popover>
      </div>

      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-red-500/10 border border-red-500/30">
        <input
          type="checkbox"
          checked={formData.is_full_day}
          onChange={(e) => setFormData({ ...formData, is_full_day: e.target.checked })}
          className="w-5 h-5 rounded border-red-500/50 accent-red-500"
        />
        <div>
          <span className="font-manrope text-red-400 font-medium">Block Entire Day</span>
          <p className="font-manrope text-neutral-400 text-xs">No appointments will be available this day</p>
        </div>
      </label>

      {!formData.is_full_day && (
        <div className="grid grid-cols-2 gap-4">
          {/* Start Time Picker */}
          <div>
            <label className="font-manrope text-neutral-400 text-sm block mb-2">Start Time *</label>
            <Popover open={startTimeOpen} onOpenChange={setStartTimeOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-left flex items-center justify-between hover:border-gold/50 focus:border-gold/50 focus:outline-none transition-colors"
                >
                  <span className={formData.start_time ? 'text-white' : 'text-neutral-500'}>
                    {formatTimeDisplay(formData.start_time)}
                  </span>
                  <Clock size={18} className="text-neutral-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0 bg-[#1a1a1a] border-white/10 max-h-64 overflow-y-auto" align="start">
                <div className="py-1">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, start_time: slot.value });
                        setStartTimeOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                        formData.start_time === slot.value ? 'bg-gold/20 text-gold' : 'text-white'
                      }`}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* End Time Picker */}
          <div>
            <label className="font-manrope text-neutral-400 text-sm block mb-2">End Time *</label>
            <Popover open={endTimeOpen} onOpenChange={setEndTimeOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-left flex items-center justify-between hover:border-gold/50 focus:border-gold/50 focus:outline-none transition-colors"
                >
                  <span className={formData.end_time ? 'text-white' : 'text-neutral-500'}>
                    {formatTimeDisplay(formData.end_time)}
                  </span>
                  <Clock size={18} className="text-neutral-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0 bg-[#1a1a1a] border-white/10 max-h-64 overflow-y-auto" align="start">
                <div className="py-1">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, end_time: slot.value });
                        setEndTimeOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                        formData.end_time === slot.value ? 'bg-gold/20 text-gold' : 'text-white'
                      }`}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      <div>
        <label className="font-manrope text-neutral-400 text-sm block mb-2">Reason (optional)</label>
        <input
          type="text"
          placeholder="e.g., Vacation, Holiday, Personal"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-3 rounded-lg bg-gold text-black font-manrope font-medium hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          Block Time
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-3 rounded-lg bg-white/10 text-white font-manrope hover:bg-white/20 transition-all"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [services, setServices] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [appointmentView, setAppointmentView] = useState('list'); // 'list' or 'calendar'
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [showBlockedTimeForm, setShowBlockedTimeForm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [autoConfirm, setAutoConfirm] = useState(() => {
    const saved = localStorage.getItem('autoConfirmAppointments');
    return saved === 'true';
  });
  const navigate = useNavigate();

  // Fetch auto-confirm setting from backend on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`);
        const backendValue = res.data?.auto_confirm_appointments || false;
        setAutoConfirm(backendValue);
        localStorage.setItem('autoConfirmAppointments', backendValue.toString());
      } catch (err) {
        console.error('Failed to fetch admin settings:', err);
      }
    };
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated]);

  // Save auto-confirm setting to localStorage and backend
  const toggleAutoConfirm = async () => {
    const newValue = !autoConfirm;
    setAutoConfirm(newValue);
    localStorage.setItem('autoConfirmAppointments', newValue.toString());
    
    // Save to backend settings
    try {
      await axios.post(`${API}/admin/settings`, { auto_confirm_appointments: newValue });
    } catch (err) {
      console.error('Failed to save auto-confirm setting:', err);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    const expires = localStorage.getItem('adminTokenExpires');

    if (token && expires) {
      const expiresDate = new Date(expires);
      if (expiresDate > new Date()) {
        try {
          const response = await axios.post(`${API}/admin/verify?token=${token}`);
          if (response.data.valid) {
            setIsAuthenticated(true);
            fetchData();
          }
        } catch (error) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminTokenExpires');
        }
      } else {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminTokenExpires');
      }
    }
    setCheckingAuth(false);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    fetchData();
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        await axios.post(`${API}/admin/logout?token=${token}`);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpires');
    setIsAuthenticated(false);
  };

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes, appointmentsRes, analyticsRes, servicesRes, galleryRes, reviewsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/products?active_only=false`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/admin/appointments`),
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/services/all`),
        axios.get(`${API}/gallery/all`),
        axios.get(`${API}/reviews/all`),
        axios.get(`${API}/blocked-times`)
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setAppointments(appointmentsRes.data);
      setAnalytics(analyticsRes.data);
      setServices(servicesRes.data);
      setGalleryItems(galleryRes.data);
      setReviews(reviewsRes.data);
      setBlockedTimes(blockedRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteBlockedTime = async (blockedId) => {
    if (!window.confirm('Are you sure you want to remove this blocked time?')) return;
    
    try {
      await axios.delete(`${API}/blocked-times/${blockedId}`);
      setBlockedTimes(prev => prev.filter(b => b.id !== blockedId));
    } catch (error) {
      console.error('Error deleting blocked time:', error);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status?status=${status}`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 size={40} className="text-gold animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLoginPage onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 size={40} className="text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0d0d0d] border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-all"
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </button>
        <h1 className="font-playfair text-xl text-white">Admin <span className="text-gold">Panel</span></h1>
        <button
          onClick={handleLogout}
          className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
        >
          <X size={20} />
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex">
        {/* Vertical Sidebar Navigation */}
        <aside className={`
          w-64 min-h-screen bg-[#0d0d0d] border-r border-white/10 fixed left-0 top-0 overflow-y-auto z-50
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}>
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h1 className="font-playfair text-2xl text-white">Admin <span className="text-gold">Panel</span></h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 transition-all"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Navigation Items */}
          <nav className="p-4 space-y-2">
            <button
              onClick={() => { setActiveTab('analytics'); setSidebarOpen(false); }}
              data-testid="tab-analytics"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 ${
                activeTab === 'analytics' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <BarChart3 size={18} />
              Analytics
            </button>
            <button
              onClick={() => { setActiveTab('appointments'); setSidebarOpen(false); }}
              data-testid="tab-appointments"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 justify-between ${
                activeTab === 'appointments' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-3">
                <CalendarCheck size={18} />
                Appointments
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'appointments' ? 'bg-black/20' : 'bg-white/10'}`}>
                {appointments.length}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('services'); setSidebarOpen(false); }}
              data-testid="tab-services"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 justify-between ${
                activeTab === 'services' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-3">
                <Sparkles size={18} />
                Services
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'services' ? 'bg-black/20' : 'bg-white/10'}`}>
                {services.length}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('gallery'); setSidebarOpen(false); }}
              data-testid="tab-gallery"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 justify-between ${
                activeTab === 'gallery' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-3">
                <Instagram size={18} />
                Gallery
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'gallery' ? 'bg-black/20' : 'bg-white/10'}`}>
                {galleryItems.length}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('reviews'); setSidebarOpen(false); }}
              data-testid="tab-reviews"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 justify-between ${
                activeTab === 'reviews' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-3">
                <Star size={18} />
                Reviews
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'reviews' ? 'bg-black/20' : 'bg-white/10'}`}>
                {reviews.length}
              </span>
            </button>
            {/* Products tab hidden 
            <button
              onClick={() => { setActiveTab('products'); setSidebarOpen(false); }}
              data-testid="tab-products"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 justify-between ${
                activeTab === 'products' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-3">
                <Package size={18} />
                Products
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'products' ? 'bg-black/20' : 'bg-white/10'}`}>
                {products.length}
              </span>
            </button>
            */}
            <button
              onClick={() => { setActiveTab('orders'); setSidebarOpen(false); }}
              data-testid="tab-orders"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 justify-between ${
                activeTab === 'orders' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="flex items-center gap-3">
                <ShoppingCart size={18} />
                Orders
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'orders' ? 'bg-black/20' : 'bg-white/10'}`}>
                {orders.length}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('availability'); setSidebarOpen(false); }}
              data-testid="tab-availability"
              className={`w-full px-4 py-3 rounded-lg font-manrope text-sm tracking-wider transition-all flex items-center gap-3 ${
                activeTab === 'availability' 
                  ? 'bg-gold text-black' 
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Clock size={18} />
              Availability
            </button>
          </nav>
          
          {/* Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-[#0d0d0d]">
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 rounded-lg font-manrope text-sm text-neutral-400 hover:text-white hover:bg-white/5 transition-all flex items-center gap-3 mb-2"
            >
              <ChevronLeft size={18} />
              Back to Site
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="w-full px-4 py-2 rounded-lg bg-red-500/10 text-red-400 text-sm font-manrope hover:bg-red-500/20 transition-all flex items-center gap-3"
            >
              <X size={18} />
              Logout
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 lg:ml-64 p-4 md:p-8 pt-20 lg:pt-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <h2 className="font-playfair text-xl md:text-2xl text-white capitalize">
              {activeTab === 'analytics' && 'Dashboard Analytics'}
              {activeTab === 'appointments' && 'Appointments'}
              {activeTab === 'services' && 'Services Management'}
              {activeTab === 'gallery' && 'Gallery Management'}
              {activeTab === 'reviews' && 'Reviews Management'}
              {activeTab === 'products' && 'Products Management'}
              {activeTab === 'orders' && 'Orders Management'}
              {activeTab === 'availability' && 'Availability Management'}
            </h2>
          </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-8">
            {/* Revenue Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gold/30 flex items-center justify-center">
                    <Sparkles size={20} className="text-gold" />
                  </div>
                  <span className="font-manrope text-gold text-sm font-medium">Service Revenue</span>
                </div>
                <p className="font-playfair text-3xl text-white">${(analytics.overview.service_revenue || 0).toFixed(2)}</p>
                <p className="font-manrope text-neutral-400 text-xs mt-1">From completed appointments</p>
              </div>
              <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center">
                    <ShoppingCart size={20} className="text-green-400" />
                  </div>
                  <span className="font-manrope text-green-400 text-sm font-medium">Product Revenue</span>
                </div>
                <p className="font-playfair text-3xl text-white">${(analytics.overview.product_revenue || 0).toFixed(2)}</p>
                <p className="font-manrope text-neutral-400 text-xs mt-1">From product orders</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border border-purple-500/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
                    <DollarSign size={20} className="text-purple-400" />
                  </div>
                  <span className="font-manrope text-purple-400 text-sm font-medium">Total Revenue</span>
                </div>
                <p className="font-playfair text-3xl text-white">${analytics.overview.total_revenue.toFixed(2)}</p>
                <p className="font-manrope text-neutral-400 text-xs mt-1">Combined revenue</p>
              </div>
            </div>

            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <CalendarCheck size={20} className="text-blue-400" />
                  </div>
                  <span className="font-manrope text-neutral-400 text-sm">Appointments</span>
                </div>
                <p className="font-playfair text-3xl text-white">{analytics.overview.total_bookings}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <ShoppingCart size={20} className="text-green-400" />
                  </div>
                  <span className="font-manrope text-neutral-400 text-sm">Orders</span>
                </div>
                <p className="font-playfair text-3xl text-white">{analytics.overview.total_orders}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Package size={20} className="text-purple-400" />
                  </div>
                  <span className="font-manrope text-neutral-400 text-sm">Products</span>
                </div>
                <p className="font-playfair text-3xl text-white">{analytics.overview.total_products}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Users size={20} className="text-orange-400" />
                  </div>
                  <span className="font-manrope text-neutral-400 text-sm">Contacts</span>
                </div>
                <p className="font-playfair text-3xl text-white">{analytics.overview.total_contacts}</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-playfair text-xl text-white mb-4">Last 7 Days</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-manrope text-neutral-400">New Appointments</span>
                    <span className="font-manrope text-gold text-xl">{analytics.recent_activity.bookings_last_7_days}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-manrope text-neutral-400">New Orders</span>
                    <span className="font-manrope text-gold text-xl">{analytics.recent_activity.orders_last_7_days}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-playfair text-xl text-white mb-4">Appointments by Type</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-blue-400" />
                      <span className="font-manrope text-neutral-400">Calendar</span>
                    </div>
                    <span className="font-manrope text-white">{analytics.bookings_by_type.calendar}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle size={16} className="text-gold" />
                      <span className="font-manrope text-neutral-400">Chat</span>
                    </div>
                    <span className="font-manrope text-white">{analytics.bookings_by_type.chat || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={16} className="text-green-400" />
                      <span className="font-manrope text-neutral-400">Text</span>
                    </div>
                    <span className="font-manrope text-white">{analytics.bookings_by_type.text}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PhoneCall size={16} className="text-purple-400" />
                      <span className="font-manrope text-neutral-400">Call</span>
                    </div>
                    <span className="font-manrope text-white">{analytics.bookings_by_type.call}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Breakdowns */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-playfair text-xl text-white mb-4">Appointment Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm">Pending</span>
                    <span className="font-manrope text-white">{analytics.bookings_by_status.pending}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">Confirmed</span>
                    <span className="font-manrope text-white">{analytics.bookings_by_status.confirmed}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">Completed</span>
                    <span className="font-manrope text-white">{analytics.bookings_by_status.completed}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-playfair text-xl text-white mb-4">Order Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm">Pending</span>
                    <span className="font-manrope text-white">{analytics.orders_by_status.pending}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">Paid</span>
                    <span className="font-manrope text-white">{analytics.orders_by_status.paid}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">Processing</span>
                    <span className="font-manrope text-white">{analytics.orders_by_status.processing}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm">Shipped</span>
                    <span className="font-manrope text-white">{analytics.orders_by_status.shipped}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm">Delivered</span>
                    <span className="font-manrope text-white">{analytics.orders_by_status.delivered}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="space-y-6">
            {/* View Toggle and Auto-Confirm */}
            <div className="flex items-center justify-between mb-2 flex-wrap gap-4">
              <h3 className="font-playfair text-xl text-white">
                {appointmentView === 'calendar' ? 'Calendar View' : 'All Appointments'}
              </h3>
              <div className="flex items-center gap-4">
                {/* Auto-Confirm Toggle */}
                <button
                  onClick={toggleAutoConfirm}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-manrope text-sm transition-all ${
                    autoConfirm 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-white/5 text-neutral-400 border border-white/10 hover:text-white'
                  }`}
                  data-testid="auto-confirm-toggle"
                >
                  <div className={`w-8 h-4 rounded-full relative transition-all ${autoConfirm ? 'bg-green-500' : 'bg-neutral-600'}`}>
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${autoConfirm ? 'left-4' : 'left-0.5'}`} />
                  </div>
                  Auto-Confirm
                </button>
                
                {/* List/Calendar Toggle */}
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setAppointmentView('list')}
                    className={`px-3 py-1.5 rounded-md font-manrope text-sm flex items-center gap-2 transition-all ${
                      appointmentView === 'list' ? 'bg-gold text-black' : 'text-neutral-400 hover:text-white'
                    }`}
                    data-testid="view-list-btn"
                  >
                    <List size={16} />
                    List
                  </button>
                  <button
                    onClick={() => setAppointmentView('calendar')}
                    className={`px-3 py-1.5 rounded-md font-manrope text-sm flex items-center gap-2 transition-all ${
                      appointmentView === 'calendar' ? 'bg-gold text-black' : 'text-neutral-400 hover:text-white'
                    }`}
                    data-testid="view-calendar-btn"
                  >
                    <Grid size={16} />
                    Calendar
                  </button>
                </div>
              </div>
            </div>

            {appointments.length === 0 ? (
              <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                <CalendarCheck size={48} className="text-neutral-600 mx-auto mb-4" />
                <p className="font-manrope text-neutral-400">No appointments yet</p>
              </div>
            ) : appointmentView === 'calendar' ? (
              <AppointmentCalendarView 
                appointments={appointments}
                onSelectAppointment={(apt) => console.log('Selected:', apt)}
              />
            ) : (
              <div className="space-y-4">
                {appointments.map((appointment) => (
                  <AppointmentCard 
                    key={appointment.id} 
                    appointment={appointment} 
                    onUpdate={fetchData}
                    services={services}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-playfair text-xl text-white">Manage Products</h3>
              <button
                onClick={() => { setEditingProduct(null); setShowProductForm(true); }}
                className="px-4 py-2 rounded-lg bg-gold text-black font-manrope text-sm font-medium hover:bg-gold/90 transition-all flex items-center gap-2"
              >
                <Plus size={16} />
                Add Product
              </button>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                <Package size={48} className="text-neutral-600 mx-auto mb-4" />
                <p className="font-manrope text-neutral-400">No products yet. Add your first product!</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {products.map((product) => (
                  <div key={product.id} className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-lg">
                    <img src={product.image_url} alt={product.name} className="w-16 h-16 object-cover rounded-lg" />
                    <div className="flex-1">
                      <h3 className="font-manrope text-white font-medium">{product.name}</h3>
                      <p className="font-manrope text-gold">${product.price.toFixed(2)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-manrope ${product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => { setEditingProduct(product); setShowProductForm(true); }}
                      className="px-4 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-all"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteProduct(product.id)}
                      className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-all"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="grid gap-4">
            {orders.length === 0 ? (
              <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                <ShoppingCart size={48} className="text-neutral-600 mx-auto mb-4" />
                <p className="font-manrope text-neutral-400">No orders yet</p>
              </div>
            ) : (
              orders.map((order) => (
                <div key={order.id} className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-manrope text-white font-medium">{order.customer_name}</p>
                      <p className="font-manrope text-neutral-400 text-sm">{order.customer_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-manrope text-gold font-semibold">${order.total.toFixed(2)}</p>
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className="mt-1 px-3 py-1 rounded bg-white/10 text-white text-sm border border-white/20"
                      >
                        <option value="pending" className="bg-[#1a1a1a] text-white">Pending</option>
                        <option value="paid" className="bg-[#1a1a1a] text-white">Paid</option>
                        <option value="processing" className="bg-[#1a1a1a] text-white">Processing</option>
                        <option value="shipped" className="bg-[#1a1a1a] text-white">Shipped</option>
                        <option value="delivered" className="bg-[#1a1a1a] text-white">Delivered</option>
                        <option value="cancelled" className="bg-[#1a1a1a] text-white">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-3">
                    <p className="font-manrope text-neutral-400 text-sm mb-2">Items:</p>
                    {order.items.map((item, idx) => (
                      <p key={idx} className="font-manrope text-white text-sm">
                        {item.quantity}x {item.name} - ${item.subtotal.toFixed(2)}
                      </p>
                    ))}
                    <p className="font-manrope text-neutral-400 text-sm mt-2">
                      Shipping: {order.shipping_address}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-playfair text-xl text-white">Manage Availability</h3>
              <button
                onClick={() => setShowBlockedTimeForm(true)}
                className="px-4 py-2 rounded-lg bg-gold text-black font-manrope text-sm font-medium hover:bg-gold/90 transition-all flex items-center gap-2"
              >
                <Plus size={16} />
                Block Time
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Block Time Form */}
              {showBlockedTimeForm && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="font-playfair text-lg text-white mb-4">Block New Time</h4>
                  <BlockedTimeForm 
                    onSave={() => { setShowBlockedTimeForm(false); fetchData(); }}
                    onCancel={() => setShowBlockedTimeForm(false)}
                  />
                </div>
              )}

              {/* Blocked Times List */}
              <div className={`bg-white/5 border border-white/10 rounded-xl p-6 ${showBlockedTimeForm ? '' : 'lg:col-span-2'}`}>
                <h4 className="font-playfair text-lg text-white mb-4">Blocked Dates & Times</h4>
                {blockedTimes.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock size={48} className="text-neutral-600 mx-auto mb-4" />
                    <p className="font-manrope text-neutral-400">No blocked times yet</p>
                    <p className="font-manrope text-neutral-500 text-sm mt-1">Block days off or specific time slots</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blockedTimes
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((blocked) => (
                      <div key={blocked.id} className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${blocked.is_full_day ? 'bg-red-500/20' : 'bg-orange-500/20'}`}>
                            {blocked.is_full_day ? (
                              <X size={20} className="text-red-400" />
                            ) : (
                              <Clock size={20} className="text-orange-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-manrope text-white font-medium">
                              {formatDateWithDay(blocked.date)}
                              {blocked.is_full_day && <span className="ml-2 text-red-400 text-sm">(Full Day)</span>}
                            </p>
                            {!blocked.is_full_day && blocked.start_time && (
                              <p className="font-manrope text-neutral-400 text-sm">
                                {formatTime(blocked.start_time)} - {formatTime(blocked.end_time)}
                              </p>
                            )}
                            {blocked.reason && (
                              <p className="font-manrope text-neutral-500 text-xs mt-1">{blocked.reason}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteBlockedTime(blocked.id)}
                          className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-all"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-playfair text-xl text-white">Manage Services</h3>
              <button
                onClick={() => { setEditingService(null); setShowServiceForm(true); }}
                className="px-4 py-2 bg-gold text-black rounded-lg font-manrope text-sm hover:bg-gold/90 flex items-center gap-2"
              >
                <Plus size={16} /> Add Service
              </button>
            </div>
            <div className="grid gap-4">
              {services.length === 0 ? (
                <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                  <Sparkles size={48} className="text-neutral-600 mx-auto mb-4" />
                  <p className="font-manrope text-neutral-400">No services yet. Add your first service!</p>
                </div>
              ) : (
                services.map((service) => (
                  <div key={service.id} className="p-4 bg-white/5 border border-white/10 rounded-lg flex gap-4">
                    <img src={service.image_url} alt={service.name} className="w-24 h-24 object-cover rounded-lg" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-playfair text-lg text-white">{service.name}</h4>
                            {service.is_featured && (
                              <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full font-manrope">Featured</span>
                            )}
                          </div>
                          <p className="font-manrope text-neutral-400 text-sm">{service.short_description}</p>
                          <div className="flex gap-4 mt-2">
                            <span className="font-manrope text-gold text-sm">{service.price_range}</span>
                            <span className="font-manrope text-neutral-500 text-sm flex items-center gap-1">
                              <Clock size={14} />
                              {(() => {
                                const mins = service.duration_minutes || 60;
                                if (mins >= 60) {
                                  const hrs = Math.floor(mins / 60);
                                  const remainingMins = mins % 60;
                                  return remainingMins > 0 ? `${hrs}hr ${remainingMins}min` : `${hrs}hr${hrs > 1 ? 's' : ''}`;
                                }
                                return `${mins}min`;
                              })()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setEditingService(service); setShowServiceForm(true); }}
                            className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Delete this service?')) {
                                await axios.delete(`${API}/services/${service.id}`);
                                fetchData();
                              }
                            }}
                            className="p-2 bg-red-500/20 rounded-lg text-red-400 hover:bg-red-500/30"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-playfair text-xl text-white">Manage Gallery</h3>
              <button
                onClick={() => { setEditingGallery(null); setShowGalleryForm(true); }}
                className="px-4 py-2 bg-gold text-black rounded-lg font-manrope text-sm hover:bg-gold/90 flex items-center gap-2"
              >
                <Plus size={16} /> Add Image
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryItems.length === 0 ? (
                <div className="col-span-full text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                  <Instagram size={48} className="text-neutral-600 mx-auto mb-4" />
                  <p className="font-manrope text-neutral-400">No gallery items yet. Add your first image!</p>
                </div>
              ) : (
                galleryItems.map((item) => (
                  <div key={item.id} className="group relative aspect-square rounded-lg overflow-hidden border border-white/10">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                    {item.is_featured && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-gold text-black text-xs rounded-full font-manrope">⭐</div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                      <p className="font-manrope text-white text-sm font-medium truncate">{item.title}</p>
                      <p className="font-manrope text-gold text-xs">{item.category}</p>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => { setEditingGallery(item); setShowGalleryForm(true); }}
                          className="flex-1 py-1 bg-white/20 rounded text-white text-xs hover:bg-white/30"
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Delete this image?')) {
                              await axios.delete(`${API}/gallery/${item.id}`);
                              fetchData();
                            }
                          }}
                          className="py-1 px-2 bg-red-500/30 rounded text-red-400 text-xs hover:bg-red-500/40"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-playfair text-xl text-white">Manage Reviews</h3>
              <button
                onClick={() => { setEditingReview(null); setShowReviewForm(true); }}
                className="px-4 py-2 bg-gold text-black rounded-lg font-manrope text-sm hover:bg-gold/90 flex items-center gap-2"
              >
                <Plus size={16} /> Add Review
              </button>
            </div>
            <div className="grid gap-4">
              {reviews.length === 0 ? (
                <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                  <Star size={48} className="text-neutral-600 mx-auto mb-4" />
                  <p className="font-manrope text-neutral-400">No reviews yet. Add your first review!</p>
                </div>
              ) : (
                reviews.map((review) => (
                  <div key={review.id} className="p-4 bg-white/5 border border-white/10 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        {review.avatar_url ? (
                          <img src={review.avatar_url} alt={review.author} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-playfair text-lg">
                            {review.author.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-manrope text-white font-medium">{review.author}</h4>
                            {review.is_featured && (
                              <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full font-manrope">⭐ Featured</span>
                            )}
                          </div>
                          <div className="flex gap-0.5 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={14} className={i < review.rating ? 'text-gold fill-gold' : 'text-neutral-600'} />
                            ))}
                          </div>
                          <p className="font-manrope text-neutral-300 text-sm italic">"{review.text}"</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingReview(review); setShowReviewForm(true); }}
                          className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20"
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Delete this review?')) {
                              await axios.delete(`${API}/reviews/${review.id}`);
                              fetchData();
                            }
                          }}
                          className="p-2 bg-red-500/20 rounded-lg text-red-400 hover:bg-red-500/30"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Product Form Modal */}
        {showProductForm && (
          <ProductFormModal
            product={editingProduct}
            onClose={() => setShowProductForm(false)}
            onSave={() => { setShowProductForm(false); fetchData(); }}
          />
        )}

        {/* Service Form Modal */}
        {showServiceForm && (
          <ServiceFormModal
            service={editingService}
            onClose={() => setShowServiceForm(false)}
            onSave={() => { setShowServiceForm(false); fetchData(); }}
          />
        )}

        {/* Gallery Form Modal */}
        {showGalleryForm && (
          <GalleryFormModal
            item={editingGallery}
            onClose={() => setShowGalleryForm(false)}
            onSave={() => { setShowGalleryForm(false); fetchData(); }}
          />
        )}

        {/* Review Form Modal */}
        {showReviewForm && (
          <ReviewFormModal
            review={editingReview}
            onClose={() => setShowReviewForm(false)}
            onSave={() => { setShowReviewForm(false); fetchData(); }}
          />
        )}
        </main>
      </div>
    </div>
  );
};

const ProductFormModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    image_url: product?.image_url || '',
    is_active: product?.is_active ?? true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (product) {
        await axios.put(`${API}/products/${product.id}`, formData);
      } else {
        await axios.post(`${API}/products`, formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save product');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl text-white">{product ? 'Edit' : 'Add'} <span className="text-gold">Product</span></h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Product Name *"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <textarea
            placeholder="Description *"
            required
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none resize-none"
          />
          <input
            type="number"
            step="0.01"
            placeholder="Price *"
            required
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <ImageUpload
            label="Product Image *"
            value={formData.image_url}
            onChange={(url) => setFormData({ ...formData, image_url: url })}
          />
          {product && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-white/20"
              />
              <span className="font-manrope text-white">Active (visible in shop)</span>
            </label>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : (product ? 'Update Product' : 'Add Product')}
          </button>
        </form>
      </div>
    </div>
  );
};

// Service Form Modal
const ServiceFormModal = ({ service, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: service?.name || '',
    description: service?.description || '',
    short_description: service?.short_description || '',
    image_url: service?.image_url || '',
    price_range: service?.price_range || '',
    duration: service?.duration || '',
    duration_minutes: service?.duration_minutes || 60,
    features: service?.features?.join('\n') || '',
    is_active: service?.is_active ?? true,
    is_featured: service?.is_featured ?? false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Duration presets for quick selection
  const durationPresets = [
    { label: '30 min', value: 30 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 },
    { label: '2.5 hours', value: 150 },
    { label: '3 hours', value: 180 },
    { label: '4 hours', value: 240 },
  ];

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const submitData = {
      ...formData,
      features: formData.features.split('\n').filter(f => f.trim())
    };

    try {
      if (service) {
        await axios.put(`${API}/services/${service.id}`, submitData);
      } else {
        await axios.post(`${API}/services`, submitData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save service');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl text-white">{service ? 'Edit' : 'Add'} <span className="text-gold">Service</span></h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Service Name *"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <input
            type="text"
            placeholder="Short Description"
            value={formData.short_description}
            onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <textarea
            placeholder="Full Description *"
            required
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none resize-none"
          />
          <ImageUpload
            label="Service Image *"
            value={formData.image_url}
            onChange={(url) => setFormData({ ...formData, image_url: url })}
          />
          <input
            type="text"
            placeholder="Price Range (e.g. $50 - $150)"
            value={formData.price_range}
            onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          
          {/* Duration Section */}
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <label className="font-manrope text-neutral-400 text-sm block mb-2">
              Appointment Duration *
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {durationPresets.map(preset => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, duration_minutes: preset.value, duration: preset.label })}
                  className={`px-3 py-1.5 rounded-lg text-sm font-manrope transition-all ${
                    formData.duration_minutes === preset.value
                      ? 'bg-gold text-black'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="15"
                max="480"
                step="15"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                className="w-24 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-center focus:border-gold/50 focus:outline-none"
              />
              <span className="font-manrope text-neutral-400 text-sm">minutes</span>
              <input
                type="text"
                placeholder="Display text (e.g. 1-2 hours)"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none text-sm"
              />
            </div>
            <p className="font-manrope text-neutral-500 text-xs mt-2">
              This duration is used to calculate available time slots when clients book this service
            </p>
          </div>

          <textarea
            placeholder="Features (one per line)"
            rows={3}
            value={formData.features}
            onChange={(e) => setFormData({ ...formData, features: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none resize-none"
          />
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-gold/10 border border-gold/30">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-5 h-5 rounded border-gold/50 accent-gold"
            />
            <div>
              <span className="font-manrope text-gold font-medium">Featured on Home Page</span>
              <p className="font-manrope text-neutral-400 text-xs">This service will appear in the Services section on home</p>
            </div>
          </label>
          {service && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-white/20"
              />
              <span className="font-manrope text-white">Active (visible on website)</span>
            </label>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : (service ? 'Update Service' : 'Add Service')}
          </button>
        </form>
      </div>
    </div>
  );
};

// Gallery Form Modal
const GalleryFormModal = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    description: item?.description || '',
    image_url: item?.image_url || '',
    category: item?.category || '',
    tags: item?.tags?.join(', ') || '',
    is_active: item?.is_active ?? true,
    is_featured: item?.is_featured ?? false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const submitData = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      if (item) {
        await axios.put(`${API}/gallery/${item.id}`, submitData);
      } else {
        await axios.post(`${API}/gallery`, submitData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save gallery item');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl text-white">{item ? 'Edit' : 'Add'} <span className="text-gold">Gallery Image</span></h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Title *"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <textarea
            placeholder="Description"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none resize-none"
          />
          <ImageUpload
            label="Gallery Image *"
            value={formData.image_url}
            onChange={(url) => setFormData({ ...formData, image_url: url })}
          />
          <input
            type="text"
            placeholder="Category (e.g. Color, Cut, Style)"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
          />
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-gold/10 border border-gold/30">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-5 h-5 rounded border-gold/50 accent-gold"
            />
            <div>
              <span className="font-manrope text-gold font-medium">⭐ Featured on Home Page</span>
              <p className="font-manrope text-neutral-400 text-xs">This image will appear in the Gallery section on home</p>
            </div>
          </label>
          {item && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-white/20"
              />
              <span className="font-manrope text-white">Active (visible in gallery)</span>
            </label>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : (item ? 'Update Image' : 'Add Image')}
          </button>
        </form>
      </div>
    </div>
  );
};

// Review Form Modal
const ReviewFormModal = ({ review, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    author: review?.author || '',
    text: review?.text || '',
    rating: review?.rating || 5,
    avatar_url: review?.avatar_url || '',
    is_featured: review?.is_featured ?? true,
    is_active: review?.is_active ?? true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (review) {
        await axios.put(`${API}/reviews/${review.id}`, formData);
      } else {
        await axios.post(`${API}/reviews`, formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save review');
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" 
      onClick={onClose}
      data-testid="review-form-modal-overlay"
    >
      <div
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="review-form-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl text-white">{review ? 'Edit' : 'Add'} <span className="text-gold">Review</span></h2>
          <button 
            onClick={onClose} 
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
            data-testid="review-form-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Author Name *"
            required
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none"
            data-testid="review-author-input"
          />
          <textarea
            placeholder="Review Text *"
            required
            rows={4}
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-neutral-500 focus:border-gold/50 focus:outline-none resize-none"
            data-testid="review-text-input"
          />
          <ImageUpload
            label="Avatar Image (optional)"
            value={formData.avatar_url}
            onChange={(url) => setFormData({ ...formData, avatar_url: url })}
          />
          
          {/* Rating Selector */}
          <div className="flex flex-col gap-2">
            <label className="font-manrope text-neutral-400 text-sm">Rating</label>
            <div className="flex gap-2" data-testid="review-rating-selector">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="p-2 transition-colors"
                  data-testid={`review-rating-${star}`}
                >
                  <Star 
                    size={24} 
                    className={star <= formData.rating ? 'text-gold fill-gold' : 'text-neutral-600'} 
                  />
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-gold/10 border border-gold/30">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-5 h-5 rounded border-gold/50 accent-gold"
              data-testid="review-featured-checkbox"
            />
            <div>
              <span className="font-manrope text-gold font-medium">Featured on Home Page</span>
              <p className="font-manrope text-neutral-400 text-xs">This review will appear in the Reviews section on home</p>
            </div>
          </label>
          
          {review && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-white/20"
                data-testid="review-active-checkbox"
              />
              <span className="font-manrope text-white">Active (visible on website)</span>
            </label>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 rounded-full bg-gold text-black font-manrope font-medium tracking-wider uppercase hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="review-submit-btn"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : (review ? 'Update Review' : 'Add Review')}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ SERVICES PORTFOLIO PAGE ============

const ServicesPortfolioPage = ({ onBookClick }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 size={48} className="text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
        <button 
          onClick={() => navigate('/')} 
          className="font-manrope text-neutral-400 hover:text-gold transition-colors mb-8 flex items-center gap-2"
        >
          <ChevronLeft size={20} /> Back to Home
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">Our</p>
          <h1 className="font-playfair text-4xl md:text-5xl text-white">SERVICES</h1>
          <p className="font-manrope text-neutral-400 mt-4 max-w-xl mx-auto">
            Explore our full range of professional hair services. Click on any image to learn more.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              onClick={() => setSelectedService(service)}
              className="group relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer"
            >
              <img
                src={service.image_url}
                alt={service.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                <h3 className="font-playfair text-lg text-white">{service.name}</h3>
                {service.price_range && (
                  <p className="font-manrope text-gold text-sm mt-1">{service.price_range}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Service Detail Modal */}
      {selectedService && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setSelectedService(null)}
        >
          <div 
            className="bg-neutral-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid md:grid-cols-2">
              {/* Image */}
              <div className="aspect-[4/5] md:aspect-auto">
                <img src={selectedService.image_url} alt={selectedService.name} className="w-full h-full object-cover" />
              </div>
              
              {/* Content */}
              <div className="p-6 md:p-8 flex flex-col max-h-[50vh] md:max-h-[90vh] overflow-y-auto">
                <button
                  onClick={() => setSelectedService(null)}
                  className="self-end w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white mb-4"
                >
                  <X size={18} />
                </button>
                
                <p className="font-caveat text-gold text-xl mb-1">Our Service</p>
                <h2 className="font-playfair text-3xl text-white mb-4">{selectedService.name}</h2>
                
                <div className="flex gap-6 mb-6">
                  {selectedService.price_range && (
                    <div>
                      <p className="font-manrope text-neutral-400 text-sm">Price Range</p>
                      <p className="font-playfair text-gold text-lg">{selectedService.price_range}</p>
                    </div>
                  )}
                  {selectedService.duration && (
                    <div>
                      <p className="font-manrope text-neutral-400 text-sm">Duration</p>
                      <p className="font-playfair text-white text-lg">{selectedService.duration}</p>
                    </div>
                  )}
                </div>
                
                <p className="font-manrope text-neutral-300 leading-relaxed mb-6">{selectedService.description}</p>
                
                {selectedService.features && selectedService.features.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-playfair text-lg text-white mb-3">What's Included</h3>
                    <ul className="space-y-2">
                      {selectedService.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 font-manrope text-neutral-300 text-sm">
                          <Check size={16} className="text-gold flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <button
                  onClick={() => { setSelectedService(null); onBookClick(); }}
                  className="mt-auto px-6 py-3 bg-gold text-black font-manrope font-medium tracking-wider uppercase rounded-full hover:bg-gold/90 transition-all"
                >
                  Book This Service
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ GALLERY PORTFOLIO PAGE ============

const GalleryPortfolioPage = ({ onBookClick }) => {
  const navigate = useNavigate();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryItems(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(galleryItems.map(item => item.category).filter(Boolean))];
  
  // Filter items by category
  const filteredItems = filter === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 size={48} className="text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
        <button 
          onClick={() => navigate('/')} 
          className="font-manrope text-neutral-400 hover:text-gold transition-colors mb-8 flex items-center gap-2"
        >
          <ChevronLeft size={20} /> Back to Home
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <p className="font-caveat text-gold text-2xl md:text-3xl mb-2">take a look at</p>
          <h1 className="font-playfair text-4xl md:text-5xl text-white">OUR WORK</h1>
          <p className="font-manrope text-neutral-400 mt-4 max-w-xl mx-auto">
            Browse our portfolio of stunning transformations. Click on any image to see details.
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 2 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                  filter === cat 
                    ? 'bg-gold text-black' 
                    : 'bg-white/10 text-neutral-400 hover:text-white'
                }`}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedItem(item)}
              className="group relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer"
            >
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                <h3 className="font-playfair text-lg text-white">{item.title}</h3>
                {item.category && (
                  <p className="font-manrope text-gold text-sm mt-1">{item.category}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery Detail Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-neutral-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid md:grid-cols-2">
              {/* Image */}
              <div className="aspect-[4/5] md:aspect-auto">
                <img src={selectedItem.image_url} alt={selectedItem.title} className="w-full h-full object-cover" />
              </div>
              
              {/* Content */}
              <div className="p-6 md:p-8 flex flex-col max-h-[50vh] md:max-h-[90vh] overflow-y-auto">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="self-end w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white mb-4"
                >
                  <X size={18} />
                </button>
                
                {selectedItem.category && (
                  <span className="font-manrope text-gold text-sm tracking-wider uppercase mb-2">{selectedItem.category}</span>
                )}
                <h2 className="font-playfair text-3xl text-white mb-4">{selectedItem.title}</h2>
                
                {selectedItem.description && (
                  <p className="font-manrope text-neutral-300 leading-relaxed mb-6">{selectedItem.description}</p>
                )}
                
                {selectedItem.tags && selectedItem.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedItem.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-white/10 rounded-full font-manrope text-sm text-neutral-300">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={() => { setSelectedItem(null); onBookClick(); }}
                  className="mt-auto px-6 py-3 bg-gold text-black font-manrope font-medium tracking-wider uppercase rounded-full hover:bg-gold/90 transition-all"
                >
                  Book a Similar Look
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ HOME PAGE ============

const HomePage = ({ onBookClick, onContactClick, onAmberClick, onTermsClick, onPrivacyClick }) => {
  return (
    <>
      <HeroSection />
      <WelcomeSection onAmberClick={onAmberClick} />
      <ServicesSection />
      <BrandsSection />
      <ReviewsSection />
      <GallerySection />
      <Footer onContactClick={onContactClick} onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
    </>
  );
};

// ============ AMBER AI CHAT COMPONENT ============

const AmberChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `amber-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add welcome message when chat opens for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi there! ✨ I'm your Hair Consultant at The Tilted Mirror! I'm here to help you book an appointment with Amber Sky Duke - an incredible stylist with 20 years of experience in cuts, color, styling, treatments, and more. What type of hair service are you interested in, or would you like to know more about Amber?"
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I'm having a moment! 💫 Please try again or feel free to call us directly." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      
      {/* Chat Window - Bottom Right */}
      <div 
        data-testid="amber-chat-window"
        className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-neutral-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100vh - 100px)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gold/20 to-gold/10 border-b border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center">
                <Sparkles size={20} className="text-black" />
              </div>
              <div>
                <h3 className="font-playfair text-white text-lg">Hair Consultant</h3>
                <p className="font-manrope text-xs text-neutral-400">AI Hair Consultant</p>
              </div>
            </div>
            <button
              data-testid="amber-chat-close"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-neutral-800 hover:bg-neutral-700 text-white flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ minHeight: '300px', maxHeight: '400px' }}>
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  msg.role === 'user'
                    ? 'bg-gold text-black rounded-br-sm'
                    : 'bg-neutral-800 text-white rounded-bl-sm'
                }`}
              >
                <p className="font-manrope text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-neutral-800 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <input
              data-testid="amber-chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask our Hair Consultant anything..."
              className="flex-1 bg-neutral-800 text-white rounded-full px-4 py-2.5 font-manrope text-sm placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold/50"
              disabled={loading}
            />
            <button
              data-testid="amber-chat-send"
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full bg-gold text-black flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gold/90 transition-colors"
            >
              <Send size={18} />
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// Wrapper component to conditionally show navigation
const AppContent = () => {
  const location = useLocation();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [amberChatOpen, setAmberChatOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [cart, setCart] = useState([]);

  const isAdminPage = location.pathname === '/admin';

  return (
    <div className="App bg-dark min-h-screen">
      {!isAdminPage && <Navigation onBookClick={() => setBookingModalOpen(true)} />}
      <Routes>
        <Route path="/" element={
          <HomePage 
            onBookClick={() => setBookingModalOpen(true)} 
            onContactClick={() => setContactModalOpen(false)} 
            onAmberClick={() => setAmberChatOpen(true)}
            onTermsClick={() => setTermsModalOpen(true)}
            onPrivacyClick={() => setPrivacyModalOpen(true)}
          />
        } />
        <Route path="/services" element={<ServicesPortfolioPage onBookClick={() => setBookingModalOpen(true)} />} />
        <Route path="/gallery" element={<GalleryPortfolioPage onBookClick={() => setBookingModalOpen(true)} />} />
        {/* Shop route hidden - products tab disabled
        <Route path="/shop" element={<ShopPage cart={cart} setCart={setCart} onBookClick={() => setBookingModalOpen(true)} />} />
        */}
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      {!isAdminPage && (
        <>
          <BookingModal 
            isOpen={bookingModalOpen} 
            onClose={() => setBookingModalOpen(false)} 
            onChatClick={() => setAmberChatOpen(true)}
          />
          <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
          <AmberChat isOpen={amberChatOpen} onClose={() => setAmberChatOpen(false)} />
          <TermsModal isOpen={termsModalOpen} onClose={() => setTermsModalOpen(false)} />
          <PrivacyModal isOpen={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} />
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
